const product = (function () {
    class StorefrontClient {
        constructor({shop = '', accessToken = ''}) {
            this.accessToken = accessToken
            this.shop = shop
            this.endpoint = `https://${shop}.myshopify.com/api/2023-07/graphql`
            this.method = 'POST'
            this.headers = {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Shopify-Storefront-Access-Token': this.accessToken
            }
        }

        async fetch({query = '', variables = {}}) {
            const response = await fetch(this.endpoint, {
                method: this.method,
                headers: this.headers,
                body: JSON.stringify({
                    query: query,
                    variables: variables
                })
            })
            if (!response.ok) {
                throw response
            }
            return response.json()
        }
    }

    // short-circuit execution on other templates
    if (document.body.dataset.templateName !== "product") return;

    // selector strings for reference
    const selectors = {
        buyBox: "[data-buy-box]",
        buyBoxInner: "[data-buy-box-inner]",
        productDisplay: "[data-product-display]",
        productHandle: "[data-product-handle]",
        productTitle: "[data-product-title]",
        productSubtitle: "[data-product-subtitle]",
        stockMessage: "[data-stock-message]",
        stockSlabMessage: "[data-stock-slab-message]",
        slabOutOfStockMessage: "[data-slab-oos]",
        slabLotLink: "[data-slab-lot-link]",
        slabLotContainer: "[data-slab-lot-container]",
        tileGroupedItemsLink: "[data-tile-grouped-items-link]",
        tileGroupedItemsContainer: "[data-tile-grouped-items-container]",
        // featuredImage: '[data-featured-image]',
        imageSlider: "[data-image-slider]",
        imageStack: "[data-image-stack]",
        imageStackThumbs: "[data-image-stack-thumbs]",
        imageSlide: "[data-image-slide]",
        globalVideoSlide: "[data-global-video-slide]",
        videoSlide: "[data-video-slide]",
        zoomSlide: "[data-zoom-slide]",
        lotSlabSlider: "[data-lot-slab-slider]",
        lotTilesGridSlider: "[data-lot-tiles-grid-slider]",
        allLotsTilesGrid: "[data-all-lots-tiles-grid]",
        lotTilesSlider: "[data-lot-tiles-slider]",
        lotZoomSlide: "[data-lot-zoom-slide]",
        imageModal: "[data-image-modal]",
        lotSlabModal: "[data-lot-slab-modal]",
        lotTileModal: "[data-lot-tile-modal]",
        slabImageModal: "[data-slab-image-modal]",
        slabDetailsMobile: "[data-slab-details-mobile]",
        slabShareMobile: "[data-slab-share-mobile]",
        openSlabModal: "[data-open-slab-modal]",
        closeSlabModal: "[data-close-slab-modal]",
        slabModalSlider: "[data-slab-modal-slider]",
        slabImageSlide: "[data-slab-image-slide]",
        slabSlideCounter: "[data-slab-slide-counter]",
        modalSlider: "[data-modal-slider]",
        modalSliderThumbs: "[data-modal-slider-thumbs]",
        openZoom: "[data-open-zoom]",
        closeZoom: "[data-close-zoom]",
        initZoom: "[data-init-zoom]",
        openLotZoom: "[data-open-lot-zoom]",
        closeLotZoom: "[data-close-lot-zoom]",
        confirmationModal: "[data-confirmation-modal]",
        closeConfirmationModal: "[data-close-confirmation-modal]",
        initialsInput: "[data-confirmation-initials]",
        confirmInitials: "[data-confirm-initials]",
        sku: "[data-sku]",
        skuContainer: "[data-sku-container]",
        productionLeadTime: "[data-production-lead-time]",
        // sqFtPerPiece: '[data-sq-ft-per-piece]',
        // sqFtPerBox: '[data-sq-ft-per-box]',
        sqFtPerUnit: "[data-sq-ft-per-unit]",
        pricePerSqFt: "[data-price-per-sq-ft]",
        discountPrice: "[data-discount-price]",
        discountPricePerBox: "[data-discount-price-per-box]",
        discountPercentage: "[data-discount-percentage]",
        discountMessage: "[data-discount-message]",
        compareAtPriceContainer: "[data-compare-at-price-container]",
        priceContainer: "[data-price-container]",
        compareAtPriceContainerPerBox: "[data-compare-at-price-container-per-box]",
        priceContainerPerBox: "[data-price-container-per-box]",
        productJson: "[data-product-json]",
        productMetafieldsJson: "[data-product-metafields-json]",
        quantitySelect: "[data-quantity-select]",
        inlineQuantity: "[data-inline-quantity]",
        inlineQuantitySelect: "[data-inline-quantity-select]",
        optionNum: "[data-option-num]",
        optionGroup: "[data-option-group]",
        optionsWrapper: "[data-options-wrapper]", // button wrapper
        optionButton: "[data-option-value]",
        selectedOption: "[data-selected-option]",
        swatch: "[data-color-swatch]",
        hiddenSelect: "[data-hidden-select]",
        productForm: "[data-product-form]",
        innerForm: "[data-inner-form]",
        addToCart: "[data-add-to-cart]",
        eachAddToCart: "[data-each-atc]",
        freeSample: "[data-free-sample]",
        samplePrice: "[data-sample-price]",
        slabGrid: "[data-slab-grid]",
        slabGridContainer: "[data-slab-grid-container]",
        hideSlabView: "[data-close-slab-view]",
        viewInventory: "[data-view-inventory]",
        ATCErrorMessage: "[data-atc-error-message]",
        ATCSuccessMessage: "[data-atc-success-message]",
        ATCInquireMessage: "[data-atc-inquire-message]",
        inquireLink: "[data-inquire-link]",
        madeInAmerica: "[data-made-in-america]",
        openCalculator: "[data-open-calculator]",
        showFormula: "[data-show-formula]",
        btnSubtract: "[data-btn-subtract]",
        btnAdd: "[data-btn-add]",
        addToCartQty: "[data-add-to-cart-qty]",
        calculate: "[data-calculate]",
        //
        lotSelectorModalOpen: "[data-lot-selector-modal-open]",
        lotSelectorModal: "[data-lot-selector-modal]",
        lotSelectorModalClose: "[data-lot-selector-modal-close]",
        lotSelector: "[data-lot-selector]",
        lotChoose: "[data-lot-choose]",
        lotSelectedLabel: "[data-lot-selected-label]",
        lotSelectedRemove: "[data-lot-selected-remove]",
        //
        closeCalculator: "[data-close-calculator]",
        unitsNeeded: "[data-units-needed]",
        totalSqFt: "[data-total-sq-ft]",
        totalPrice: "[data-total-price]",
        tooltip: "[data-tooltip]",
        closeTooltip: "[data-close-tooltip]",
        tooltipContent: "[data-tooltip-content]",
        overage: "[data-overage]",
        overagePercentage: "[data-overage-percentage]",
        overageIndicator: "[data-overage-indicator]",
        variableDetail: "[data-point]",
        metafieldSource: "[data-metafield-source]",
        inquireState: "[data-inquire-state]",
        price: "[data-price]",
        pricePerBox: "[data-price-per-box]",
        sqFtNeededContainer: "[data-sq-ft-needed-container]",
        sqFtNeededContainerFormula: "[data-sq-ft-needed-container-formula]",
        sqFtInputLength: "[data-sq-ft-input-length]",
        sqFtInputWidth: "[data-sq-ft-input-width]",
        sqFtInput: "[data-sq-ft-input]",
        unitType: "[data-unit-type]",
        priceUnitType: "[data-price-unit-type]",
        optionSliderBtn: "[data-option-slider-btn]",
        tradeMember: "[data-trade-member]",
        prop: "[data-prop]",
        lotQuantity: "[data-lot-quantity]",
        lotGridButton: "[data-lot-grid-button]",
        lotVariantWrapper: "[data-lot-variant-wrapper]",
        lotSwitchGridNumbers: "[data-switch-lot-grid-numbers]",
        lotSlideView: "[data-lot-slab-slide-view]",
        lotShareBtn: "[data-lot-share-btn]",
        lotModalInfoTooltipBtn: "[data-lot-modal-info-tooltip-btn]",
        lotModalInfoTooltipClose: "[data-lot-modal-info-tooltip-close]",
        lotModalInfoTooltip: "[data-lot-modal-info-tooltip]",
        copyToCLipMessage: "[data-copied-to-clipboard]",
        updateLotSerial: "[data-update-lot-serial]",
        viewAllLotsBtn: "[data-view-lots-btn]",
        viewAllLotsTileBtn: "[data-view-lots-tile-btn]",
        viewAllLotsGridBtn: "[data-view-all-lots-grid-btn]",
        updateLotQty: "[data-update-lot-qty]",
        updateLotSize: "[data-update-lot-size]",
        notifyMe: "[data-notify-me]",
        openChat: "[data-open-chat]",
        dataGrout: "[data-grout]"
    };

    // dom cache
    let dom = undefined;

    // update these dynamically once metafield data is fetched
    let productData,
        productMetafieldsData,
        metafieldSource,
        inquireState,
        madeInAmerica,
        //
        hasCloseoutPrice = false,
        closeoutDiscount = 0,
        closeoutInvPerct = 0,
        //
        isTradeMember,
        discountPercentage = null,
        //
        productObject,
        currentVariant,
        pricePerSqFt,
        sqFtSelectedLot,
        sqFtChosenLot,
        overageApplied = false,
        sqFtAutoChosenLotQty = null,
        sqFtLotSelectorBtns = [],
        sqFtLots = [],
        sqFtPerPiece,
        sqFtPerBox,
        soldByFullBox,
        sqFtPerUnit,
        totalSqFtNeeded = 0,
        overagePercentage,
        sampleVariantData,
        lotThresholdQuantity = false,
        validatedInitials,
        lotModalOpen = false,
        openCalculator = false,
        showFormula = false,
        wishlistId = false,
        slabViewActive = false,
        slabCounterSetup = false,
        slabModalOpen = false,
        verticalScrollPosition = 0,
        focusedElement = 0,
        isSlab = false,
        isTile = false,
        $slabModalSlider,
        unitOfMeasure,
        optionSliderAnimating = false,
        atcUnitQty = 1,
        //
        isWSDevOnly = document.body.dataset.themeRole !== "main";

    const productCategories = [
        "subcategory:Grout Sanded",
        "subcategory:Sealer/Cleaner",
        "subcategory:Grout Unsanded",
        "subcategory:Grout",
        "subcategory:Additive",
        "subcategory:Flexible Sealant",
        "subcategory:Flexible Sealant Sanded",
        "subcategory:Mortar Medium Bed",
        "subcategory:Mortar Thin Set",
        "subcategory:Adhesive Epoxy",
        "subcategory:AdhesiveMastic",
        "subcategory:Adhesive R2 Reactive Resin",
        "subcategory:Membrane",
        "subcategory:Mortar",
        "subcategory:AdhesiveMirrorMastic",
        "subcategory:Flexible Sealant Unsanded",
        "subcategory:Miscellaneous",
        "subcategory:Primer",
        "subcategory:Underlayment"
    ];

    // left arrows (used in Lot slider)
    const LEFT_ARROW = `<?xml version="1.0" encoding="UTF-8"?><svg width="25px" height="15px" viewBox="0 0 25 15" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"> <g id="Homepage-Modules" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"> <g id="D---Featured-Styles" transform="translate(-1033.000000, -3266.000000)"> <g id="Group-Copy" transform="translate(1033.000000, 3266.000000)"> <line x1="1" y1="7.14078721" x2="24.0000619" y2="7.19675156" id="Line-Copy-3" stroke="#8B8D8E" stroke-width="1.25" stroke-linecap="round"></line> <path d="M6.14772233,7.18095894 L0.18068394,13.5138881 C-0.00144438789,13.7081565 -0.0500092777,13.9908781 0.0540608807,14.2354566 C0.158135479,14.4800261 0.39750084,14.639606 0.664618835,14.6413377 C0.848478927,14.6413377 1.02540308,14.5650158 1.1520439,14.433193 L8.00006191,7.16377895 L1.14005476,0.183880422 C0.879872707,-0.0641570812 0.470528012,-0.060689112 0.213827246,0.190820801 C-0.0428824012,0.442330714 -0.0532907491,0.851688731 0.189542581,1.11531655 L6.14772233,7.18095894 Z" id="Fill-1-Copy" fill="currentColor" transform="translate(4.000062, 7.320669) scale(-1, 1) translate(-4.000062, -7.320669) "></path> </g> </g> </g></svg>`;
    const LEFT_CHEVRON = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="8px" height="16px" viewBox="0 0 8 16" version="1.1"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-132.000000, -646.000000)" fill="#494742"><g transform="translate(118.000000, 437.000000)"><g transform="translate(18.500000, 217.000000) scale(-1, 1) translate(-18.500000, -217.000000) translate(0.000000, 198.000000)"><g transform="translate(19.000000, 18.776119) rotate(-90.000000) translate(-19.000000, -18.776119) translate(11.000000, 14.776119)"><polygon id="Path" points="7.99052632 8 15.78 0.311688312 15.4852632 0 7.99052632 7.3974026 0.495789474 0 0.18 0.311688312"/></g></g></g></g></g></svg>`;
    // right arrow (used in Lot slider)
    const RIGHT_ARROW = `<?xml version="1.0" encoding="UTF-8"?><svg width="25px" height="15px" viewBox="0 0 25 15" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"> <g id="Homepage-Modules" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"> <g id="D---Featured-Styles" transform="translate(-1163.000000, -3266.000000)"> <g id="Group-2" transform="translate(1033.000000, 3266.000000)"> <g id="Group" transform="translate(143.000000, 7.500000) scale(-1, 1) translate(-143.000000, -7.500000) translate(131.000000, 0.000000)"> <line x1="1" y1="7.14078721" x2="24.0000619" y2="7.19675156" id="Line-Copy-3" stroke="#8B8D8E" stroke-width="1.25" stroke-linecap="round"></line> <path d="M6.14772233,7.18095894 L0.18068394,13.5138881 C-0.00144438789,13.7081565 -0.0500092777,13.9908781 0.0540608807,14.2354566 C0.158135479,14.4800261 0.39750084,14.639606 0.664618835,14.6413377 C0.848478927,14.6413377 1.02540308,14.5650158 1.1520439,14.433193 L8.00006191,7.16377895 L1.14005476,0.183880422 C0.879872707,-0.0641570812 0.470528012,-0.060689112 0.213827246,0.190820801 C-0.0428824012,0.442330714 -0.0532907491,0.851688731 0.189542581,1.11531655 L6.14772233,7.18095894 Z" id="Fill-1-Copy" fill="currentColor" transform="translate(4.000062, 7.320669) scale(-1, 1) translate(-4.000062, -7.320669) "></path> </g> </g> </g> </g></svg>`;
    const RIGHT_CHEVRON = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="8px" height="16px" viewBox="0 0 8 16" version="1.1"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-1391.000000, -646.000000)" fill="#494742"><g transform="translate(118.000000, 437.000000)"><g transform="translate(1258.000000, 198.000000)"><g transform="translate(19.000000, 18.776119) rotate(-90.000000) translate(-19.000000, -18.776119) translate(11.000000, 14.776119)"><polygon id="Path" points="7.99052632 8 15.78 0.311688312 15.4852632 0 7.99052632 7.3974026 0.495789474 0 0.18 0.311688312"/></g></g></g></g></g></svg>`;
    // zoom icon (used in Lot Inventory table)
    const ZOOM_ICON_MARKUP = `<svg width="30px" height="30px" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg"><g id="PDP-Gallery" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g id="PDP---Gallery-1a" transform="translate(-56 -956)" fill="#000"><g id="Group-7" transform="translate(38 938)"><g id="Group-5-Copy" transform="translate(18 18)"><path d="M12.987 25.966a12.947 12.947 0 01-9.183-3.797c-5.064-5.064-5.064-13.301 0-18.365 5.064-5.063 13.3-5.064 18.365 0 5.063 5.064 5.063 13.3 0 18.365a12.948 12.948 0 01-9.182 3.797zm0-24.71a11.704 11.704 0 00-8.3 3.431c-4.576 4.577-4.576 12.021 0 16.598 4.577 4.575 12.02 4.576 16.598 0 4.576-4.576 4.576-12.021 0-16.598a11.703 11.703 0 00-8.298-3.432z" id="Fill-1"/><path d="M29.375 30c-.16 0-.32-.06-.443-.183l-7.648-7.649a.625.625 0 01.883-.883l7.649 7.649a.625.625 0 01-.44 1.066z" id="Fill-2"/><path d="M17.986 13.611h-10a.625.625 0 010-1.249h10a.625.625 0 010 1.25z" id="Fill-3"/><path d="M12.987 18.612a.625.625 0 01-.625-.625v-10a.625.625 0 011.25 0v10a.624.624 0 01-.625.625z" id="Fill-4"/></g></g></g></g></svg>`;

    // slick options
    let $lotSlabSlider = null;
    let $lotTilesGridSlider = null;
    let slidesFiltered = false;
    const IMAGE_GALLERY_OPTIONS = {
        dots: true,
        infinite: false,
        touchThreshold: 10,
        asNavFor: ".pdp__image-modal-slider",
        focusOnSelect: false,
        centerMode: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        nextArrow:
            '<svg class="arrow-right" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="8px" height="16px" viewBox="0 0 8 16" version="1.1"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-1391.000000, -646.000000)" fill="#494742"><g transform="translate(118.000000, 437.000000)"><g transform="translate(1258.000000, 198.000000)"><g transform="translate(19.000000, 18.776119) rotate(-90.000000) translate(-19.000000, -18.776119) translate(11.000000, 14.776119)"><polygon id="Path" points="7.99052632 8 15.78 0.311688312 15.4852632 0 7.99052632 7.3974026 0.495789474 0 0.18 0.311688312"/></g></g></g></g></g></svg>',
        prevArrow:
            '<svg class="arrow-left" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="8px" height="16px" viewBox="0 0 8 16" version="1.1"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-132.000000, -646.000000)" fill="#494742"><g transform="translate(118.000000, 437.000000)"><g transform="translate(18.500000, 217.000000) scale(-1, 1) translate(-18.500000, -217.000000) translate(0.000000, 198.000000)"><g transform="translate(19.000000, 18.776119) rotate(-90.000000) translate(-19.000000, -18.776119) translate(11.000000, 14.776119)"><polygon id="Path" points="7.99052632 8 15.78 0.311688312 15.4852632 0 7.99052632 7.3974026 0.495789474 0 0.18 0.311688312"/></g></g></g></g></g></svg>',
    };
    const MODAL_GALLERY_OPTIONS = {
        ...IMAGE_GALLERY_OPTIONS,
        asNavFor: `${selectors.modalSliderThumbs}`,
        dots: false,
        arrows: true,
        swipe: false,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    // swipe: false≈
                },
            },
        ],
    };
    const MODAL_THUMBS_GALLERY_OPTIONS = {
        slidesToShow: 3,
        slidesToScroll: 1,
        asNavFor: `${selectors.modalSlider}`,
        arrows: false,
        centerMode: true,
        focusOnSelect: true,
        infinite: false
    };
    const LOT_GALLERY_OPTIONS = {
        ...IMAGE_GALLERY_OPTIONS,
        infinite: true,
        dots: false,
        prevArrow: `<button class="pdp__lot-modal-slider-btn pdp__lot-modal-slider-btn--prev"><span class="btn-content"><span class="icon-chevron">${LEFT_CHEVRON}</span><span class="icon-arrow">${LEFT_ARROW}</span> Previous</span></button>`,
        nextArrow: `<button class="pdp__lot-modal-slider-btn pdp__lot-modal-slider-btn--next"><span class="btn-content">Next <span class="icon-chevron">${RIGHT_CHEVRON}</span><span class="icon-arrow">${RIGHT_ARROW}</span></span></button>`,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    // swipe: false
                },
            },
        ],
    };
    const GRID_GALLERY_OPTIONS = {
        ...IMAGE_GALLERY_OPTIONS,
        infinite: true,
        dots: false,
        prevArrow: '.pdp__lot-modal-slider-btn.pdp__lot-modal-slider-btn--prev',
        nextArrow: '.pdp__lot-modal-slider-btn.pdp__lot-modal-slider-btn--next',
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    // swipe: false
                },
            },
        ],
    };
    const SLAB_GALLERY_OPTIONS = {
        ...IMAGE_GALLERY_OPTIONS,
        dots: false,
        asNavFor: false,
        arrows: true,
    };

    /**
     * Init zoom slides in modal
     */
    const initZoomSlides = () => {
        try {
            $(selectors.zoomSlide).each((i, el) => {
                // destroy zoom instance if it's already there
                $(el).trigger("zoom.destroy");
                const zoomUrl = el.dataset.zoomUrl;
                $(el).zoom({
                    url: zoomUrl,
                    on: "click",
                    touch: true,
                    onZoomIn: function () {
                        $(el).addClass("is-zoomed");
                    },
                    onZoomOut: function () {
                        $(el).removeClass("is-zoomed");
                    },
                });
            });
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Init lot zoom slides
     */
    const initLotZoomSlides = () => {
        try {
            $(selectors.lotZoomSlide).each((i, el) => {
                if (!el.classList.contains("no-image")) {
                    // destroy zoom instance if it's already there
                    $(el).trigger("zoom.destroy");
                    const zoomUrl = el.dataset.zoomUrl;
                    $(el).zoom({
                        url: zoomUrl,
                        on: "click",
                        onZoomIn: function () {
                            $(el).addClass("is-zoomed");
                        },
                        onZoomOut: function () {
                            $(el).removeClass("is-zoomed");
                        },
                    });
                }
            });
        } catch (e) {
            console.error(e);
        }
    };

    const gotoLotSlabsGrid = (variantId, isDirectLink = false) => {
        try {
            if (isDirectLink) {
                toggleVisibility(dom.lotSlabModal, true, 'visually-');
                $("html").addClass("lot-modal-open");
                $(selectors.modalSlider).focus();
                lotModalOpen = true;
            }
            const $grid = document.querySelector(`[data-lot-grid="${variantId}"]`);
            const $variantWrappers = document.querySelectorAll(selectors.lotVariantWrapper);
            $variantWrappers.forEach((wrapper) => {
                if (wrapper.dataset.lotVariantWrapper === variantId) {
                    wrapper.style.display = "block";
                } else {
                    wrapper.style.display = "none";
                }
            });
            $grid.style.display = "block";
            $(`[data-lot-slab-slider-wrapper="${variantId}"]`).hide();
            removeLotIdFromUrl();
        } catch (e) {
            console.error(e);
        }
    };

    const gotoLotSlabsGridSlider = (variantId, lotId) => {
        try {
            const $grid = document.querySelector(`[data-lot-grid="${variantId}"]`);
            const $variantWrappers = document.querySelectorAll(selectors.lotVariantWrapper);
            $grid.style.display = "none";
            $variantWrappers.forEach((wrapper) => {
                if (wrapper.dataset.lotVariantWrapper === variantId) {
                    wrapper.style.display = "block";
                } else {
                    wrapper.style.display = "none";
                }
            });
            $(`[data-lot-slab-slider-wrapper="${variantId}"]`).show();
            initLotZoomSlides();
            updateLotModalProductSliderPosition(lotId);
        } catch (e) {
            console.error(e);
        }
    };

    const gotoLotTilesGridSlider = (variantId, lotId) => {
        try {
            $(`[data-lot-grid="${variantId}"]`).show();
            $("[data-lot-tile-slider-wrapper]").hide();
            $(`[data-lot-variant-wrapper="${variantId}"]`).show().siblings().hide();
            $lotTilesGridSlider.slick("refresh");
            // go to lot slide
            const index = $(`[data-lot-tiles-grid-slide="${lotId}"]`).data("slick-index");
            const $currentSlider = $(`[data-lot-tiles-grid-slide="${lotId}"]`).parents(selectors.lotTilesGridSlider);
            //
            setTimeout(() => {
                $currentSlider.slick("goTo", index, true); // don't animate
            }, 500);
        } catch (e) {
            console.error(e);
        }
    };

    const gotoLotTilesGridAllLots = (variantId, lotId) => {
        try {
            $(`[data-lot-grid="${variantId}"]`).hide();
            $(`[data-all-lots-grid="${variantId}"]`).show();
            $(`[data-lot-variant-wrapper="${variantId}"]`).show().siblings().hide();
            $lotTilesGridSlider.slick("refresh");
            // go to lot slide
            const index = $(`[data-all-lot-tiles-grid-slide="${lotId}"]`).data("slick-index");
            const $currentSlider = $(`[data-all-lot-tiles-grid-slide="${lotId}"]`).parents(selectors.allLotsTilesGrid);
            setTimeout(() => {
                $currentSlider.slick("goTo", index, true); // don't animate
            }, 500);
        } catch (e) {
            console.error(e);
        }
    };

    const gotoLotTilesGrid = (variantId, isDirectLink = false) => {
        try {
            if (isDirectLink) {
                toggleVisibility(dom.lotTileModal, true, 'visually-');
                $("html").addClass("lot-modal-open");
                $(selectors.modalSlider).focus();
                lotModalOpen = true;
            }

            const $grid = document.querySelector(`[data-all-lots-grid="${variantId}"]`);
            const $lotTileSlider = document.querySelector(`[data-lot-tile-slider-wrapper="${variantId}"]`);
            const $variantWrappers = document.querySelectorAll(selectors.lotVariantWrapper);
            $variantWrappers.forEach((wrapper) => {
                if (wrapper.dataset.lotVariantWrapper === variantId) {
                    wrapper.style.display = "block";
                } else {
                    wrapper.style.display = "none";
                }
            });
            $(`[data-lot-grid="${variantId}"]`).hide();
            $grid.style.display = "block";
            $lotTileSlider.style.display = "none";
            removeLotIdFromUrl();
        } catch (e) {
            console.error(e);
        }
    };

    const gotoLotTileSlider = (variantId, lotId, picIndex) => {
        try {
            $(`[data-all-lots-grid="${variantId}"]`).hide();
            const $grid = document.querySelector(`[data-lot-grid="${variantId}"]`);
            const $lotTileSlider = document.querySelector(`[data-lot-tile-slider-wrapper="${variantId}"][data-lot-serial="${lotId}"]`);
            const $variantWrappers = document.querySelectorAll(selectors.lotVariantWrapper);
            $variantWrappers.forEach((wrapper) => {
                if (wrapper.dataset.lotVariantWrapper === variantId) {
                    wrapper.style.display = "block";
                } else {
                    wrapper.style.display = "none";
                }
            });
            $grid.style.display = "none";
            $lotTileSlider.style.display = "block";

            $(`[data-lot-tiles-slider="${variantId}"][data-lot-serial="${lotId}"]`).slick("refresh");
            initLotZoomSlides();

            // Adding lot information on detailed gallery
            const container = $(`[data-lot-tile-slider-wrapper="${variantId}"][data-lot-serial="${lotId}"][data-slide-parent]`)
            const gotoSlide = $(`[data-lot-tiles-slider="${variantId}"][data-lot-serial="${lotId}"] .slick-track`).find(`[data-slick-index="${picIndex}"]`)[0]
            const lotSerial = gotoSlide.dataset.lotSerial;
            const lotSize = gotoSlide.dataset.lotSize;
            container.find(selectors.updateLotSerial).html(`Lot ${lotSerial}`)
            container.find(selectors.updateLotSize).html(`${lotSize}`);
            // END - Adding lot information on detailed gallery

            setTimeout(() => {
                $(
                    `[data-lot-tiles-slider="${variantId}"][data-lot-serial="${lotId}"]`
                ).slick("goTo", picIndex, true);
            }, 500);
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Open + close image gallery modal
     */
    const openImageModal = () => {
        try {
            $("#chat-widget-container").hide();
            toggleVisibility(dom.imageModal, true, 'visually-');
            $("html").addClass("gallery-modal-open");
            $(selectors.modalSlider).focus();
        } catch (e) {
            console.error(e);
        }
    };

    const closeImageModal = () => {
        try {
            toggleVisibility(dom.imageModal, false, 'visually-');
            $("html").removeClass("gallery-modal-open");
            $("#chat-widget-container").show();
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Open + close slab lot inventory modal
     * @param lotId
     */
    const openLotSlabModal = (lotId) => {
        try {
            // update lot modal slider to matching Lot image (before opening)
            const variantId = document
                .querySelector(`[data-lot-slab-slide="${lotId}"]`)
                .closest("[data-lot-slab-slider]").dataset.lotSlabSlider;
            updateUrl(variantId, lotId);
            gotoLotSlabsGridSlider(variantId, lotId);
            toggleVisibility(dom.lotSlabModal, true, 'visually-');
            $("html").addClass("lot-modal-open");
            $("#chat-widget-container").hide();
            $(selectors.modalSlider).focus();
            lotModalOpen = true;
        } catch (e) {
            console.error(e);
        }
    };

    const closeLotSlabModal = () => {
        try {
            toggleVisibility(dom.lotSlabModal, false, 'visually-');
            $("html").removeClass("lot-modal-open");
            //
            $("#chat-widget-container").show();
            $("[data-lot-slab-slider-wrapper]").hide();
            //
            lotModalOpen = false;
            updateUrl(null, null);
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Open + close slab lot inventory modal
     * @param lotId
     * @param variantId
     */
    const openLotTileModal = (lotId, variantId) => {
        try {
            $(`[data-all-lots-grid="${variantId}"]`).hide();
            $("#chat-widget-container").hide();
            updateUrl(variantId, lotId);
            toggleVisibility(dom.lotTileModal, true, 'visually-');
            $("html").addClass("lot-modal-open");
            $(selectors.modalSlider).focus();
            lotModalOpen = true;
            gotoLotTileSlider(variantId, lotId, 0);
        } catch (e) {
            console.error(e);
        }
    };

    const closeLotTileModal = () => {
        try {
            toggleVisibility(dom.lotTileModal, false, "visually-")
            $("html").removeClass("lot-modal-open");
            //
            $("#chat-widget-container").show();
            $("[data-lot-tile-slider-wrapper]").hide();
            //
            lotModalOpen = false;
            updateUrl(null, null);
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Open slab inventory modal
     * @param slabId
     */
    const openSlabModal = (slabId = false) => {
        try {
            $("#chat-widget-container").hide();
            // go to specific slide (if it exists)
            goToSlabSlideById(slabId);
            // show modal
            dom.slabImageModal.classList.remove("visually-hidden");
            $("html").addClass("slab-modal-open");
            slabModalOpen = true;
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Close slab inventory modal
     */
    const closeSlabModal = () => {
        try {
            toggleVisibility(dom.slabImageModal, false, 'visually-');
            goToSlabSlideById(); // first slide
            $("html").removeClass("slab-modal-open");
            $("#chat-widget-container").show();
            slabModalOpen = false;
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Passing no argument (or a non-existent slabId) navigates to the first slide
     * @param slabId
     */
    const goToSlabSlideById = (slabId = false) => {
        try {
            let matchingSlide = false;
            if (slabId) {
                const slabSlides = Array.from(document.querySelectorAll(selectors.slabImageSlide));
                matchingSlide = slabSlides.find((slide) => slide.dataset.slabId == slabId);
            }
            // default to first slide if slabId not found
            const slideNum = matchingSlide
                ? Number(matchingSlide.dataset.slickIndex)
                : 0;

            $slabModalSlider.slick("slickGoTo", slideNum, true);
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Open + close confirmation modal
     */
    const openConfirmationModal = () => {
        try {
            $("#chat-widget-container").hide();
            toggleVisibility(dom.confirmationModal, true, 'visually-');
            $("html").addClass("confirmation-modal-open");
        } catch (e) {
            console.error(e);
        }
    };

    const closeConfirmationModal = () => {
        try {
            toggleVisibility(dom.confirmationModal, false, 'visually-');
            $("html").removeClass("confirmation-modal-open");
            $("#chat-widget-container").show();
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Opens tooltip in calculator
     */
    const openTooltip = () => {
        try {
            dom.tooltip.classList.add("is-open");
            dom.tooltipContent.classList.add("is-open");
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Closes tooltip in calculator
     */
    const closeTooltip = () => {
        try {
            dom.tooltipContent.classList.remove("is-open");
            dom.tooltip.classList.remove("is-open");
        } catch (e) {
            console.error(e);
        }
    };

    const showInputError = (outOfStock = false, selectedLot = false) => {
        try {
            if (unitOfMeasure === "Each") {
                // show error on inline quantity input
                dom.inlineQuantity.classList.add("error");
            } else {
                if (outOfStock) {
                    // only show input error inside of calculator
                    document.querySelector(`.pdp__calculator ${selectors.sqFtInput}`).parentElement.classList.add("error");
                } else {
                    // show error only on the calculator input
                    Array.from(dom.sqFtInput)
                        .filter((el) => el.id === "sqFtNeeded")[0]
                        .parentElement.classList.add("error");

                    if (selectedLot) {
                        dom.lotSelectorModalOpen?.parentNode.classList.add('error');
                    }
                }
            }
        } catch (e) {
            console.error(e);
        }
    };

    /**
     *
     * @param messageMarkup
     * @param isError
     */
    const showATCMessage = (messageMarkup, isError = true) => {
        try {
            // messaging
            const message = document.createElement("p");
            message.classList.add("pdp-atc-message");
            if (isError) {
                message.classList.add("pdp-atc-message__error");
                message.dataset.atcErrorMessage = 'true';
            } else {
                message.classList.add("pdp-atc-message__success");
                message.dataset.atcSuccessMessage = 'true';
            }
            message.innerHTML = messageMarkup;
            //
            if (unitOfMeasure === "Each") {
                dom.innerForm.appendChild(message);
            } else {
                message.classList.add("calculator");
                // add under the Sq Ft Input in the Calculator
                const calculatorInput = document.querySelector(".pdp__calculator-item.sqft-item");
                const overageBlock = document.querySelector(".pdp__calculate-overage");
                if (isError) {
                    calculatorInput.classList.add("error");
                } else {
                    calculatorInput.classList.remove("error");
                }
                calculatorInput.insertBefore(message, overageBlock);
            }
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Hide ATC message
     */
    const clearErrors = () => {
        try {
            // remove error classes
            document.querySelectorAll(".error").forEach((el) =>
                el.classList.remove("error")
            );
            dom.lotSelectorModalOpen?.parentNode.classList.remove('error');

            // remove ATC messaging
            removeATCMsg(selectors.ATCErrorMessage);

            // remove appended inquire buttons
            const inquireButton = document.querySelector(selectors.inquireLink);
            if (inquireButton && !inquireButton.classList.contains("static-inquire-btn")) {
                inquireButton.parentElement.remove();
            }

            // enable add to cart
            if (currentVariant.available && dom.addToCart) {
                dom.addToCart.disabled = false;
            }
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Hide message with link to inquire about product stock
     */
    const removeATCMsg = (selector) => {
        try {
            const message = document.querySelector(selector);
            if (message) {
                message.parentNode.removeChild(message);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const mapThresholdQuantity = () => {
        try {
            const lotQuantityRows = Array.from(document.querySelectorAll(selectors.lotQuantity));
            if (!lotQuantityRows.length) {
                lotThresholdQuantity = false;
                return;
            }
            const lotQuantities = lotQuantityRows.map((el) =>
                Number(el.textContent.replace("S/F", "").trim())
            );
            lotThresholdQuantity = Math.max(...lotQuantities);
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Initialize image gallery using slick
     */
    const initializeGallerySliders = () => {
        try {
            // regular gallery slider
            const $imageSlider = $(selectors.imageSlider);
            const $modalSlider = $(selectors.modalSlider);
            const $modalSliderThumbs = $(selectors.modalSliderThumbs);

            // init sliders
            $imageSlider.toggleClass("!hidden");
            $imageSlider.slick(IMAGE_GALLERY_OPTIONS);
            $modalSlider.slick(MODAL_GALLERY_OPTIONS);
            $modalSliderThumbs.slick(MODAL_THUMBS_GALLERY_OPTIONS);

            // thumbnail click handler on modal
            $modalSliderThumbs.on('click', '.slick-slide', function (event) {
                event.preventDefault();
                var goToSingleSlide = $(this).index();

                $modalSlider.slick('slickGoTo', goToSingleSlide);
            });

            // init zoom functionality
            initZoomSlides();

            // setup click listener for modal
            $imageSlider.on("click", selectors.imageSlide, () => {
                openImageModal();
            });

            // setup autoplay listener for video
            $imageSlider.on("afterChange", (event, slick, currentSlide) => {
                if (!dom.globalVideoSlide && !dom.videoSlide.length) return;

                // if we land on a video slide, autoplay
                const currentSlideEl = $(slick.$slides.get(currentSlide)).get(0);
                if (currentSlideEl.children[0].tagName === "VIDEO") {
                    currentSlideEl.children[0].play();
                } else {
                    // pause + reset all videos
                    dom.videoSlide.forEach((el) => {
                        const video = el.querySelector("video");
                        if (video) {
                            video.pause();
                            video.currentTime = 0;
                        }
                    });
                }
            });
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Show only images related to the selected variant
     */
    const filterGallerySliders = () => {
        try {
            const selectedVariantId = currentVariant.id;
            const $imageSlider = $(selectors.imageSlider);
            const $modalSlider = $(selectors.modalSlider);
            const $modalSliderThumbs = $(selectors.modalSliderThumbs);
            // unfilter if already filtered
            if (slidesFiltered) {
                $imageSlider.slick("slickUnfilter");
                $modalSlider.slick("slickUnfilter");
                $modalSliderThumbs.slick("slickUnfilter");
                slidesFiltered = false;
            }

            // determine selector string based on whether selectedVariantId has its own video slide
            const matchingVariantVideoId = Array.from(dom.videoSlide)
                .filter((el) => el.dataset.hasOwnProperty("variantId"))
                .map((el) => el.dataset.variantId)
                .find((id) => id == selectedVariantId);

            const selectorString = matchingVariantVideoId
                ? `[data-variant-id="${selectedVariantId}"], [data-variant-id="tour"]` // show specific variant video (normal variant ID filtering)
                : `[data-variant-id="${selectedVariantId}"], [data-global-video-slide]`; // show global product video

            // filter on selector string
            $imageSlider.slick("slickFilter", selectorString);
            $modalSlider.slick("slickFilter", selectorString);
            $modalSliderThumbs.slick("slickFilter", selectorString);
            // move to first slide
            $imageSlider.slick("slickGoTo", 0, false); // go to first slide after filtering - no animation

            slidesFiltered = true;

            // hide dots when there's only 1 slide
            const sliderObject = $imageSlider.slick("getSlick");
            if (sliderObject && sliderObject.slideCount === 1) {
                $(".pdp__image-slider .slick-dots").hide();
            } else {
                $(".pdp__image-slider .slick-dots").show();
            }
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Borrowed from https://linuxhint.com/scroll-to-element-javascript/
     * @param obj
     * @returns {[number]}
     */
    const getPosition = (obj) => {
        try {
            let currentTop = -100;
            if (obj.offsetParent) {
                do {
                    currentTop += obj.offsetTop;
                } while ((obj = obj.offsetParent));
                return [currentTop];
            }
        } catch (e) {
            console.error(e);
        }
    }

    /**
     * Show images stacked in gallery on desktop
     */
    const filterStackedGallery = () => {
        try {
            if (!(new URL(document.location)).searchParams.get("customer_posted")) {
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: "smooth",
                });
            }

            const selectedVariantId = `${currentVariant.id}`;
            dom.imageStack.classList.remove("!hidden");
            const stackedImagesThumbs = dom.imageStackThumbs.querySelectorAll(".pdp__image-slide-thumb");
            let featuredSet = false;

            if (metafieldSource !== "product") {
                dom.imageStack.querySelectorAll(".pdp__image-slide").forEach((img) => {
                    if (`${img.dataset.variantId}` === selectedVariantId || `${img.dataset.variantId}` === `tour`) {
                        img.classList.remove("hidden"); // show
                        if (!featuredSet) {
                            img.classList.add("featured-image");
                            featuredSet = true;
                        }
                    } else {
                        img.classList.add("hidden"); // hide
                    }
                });
                let selected = false;
                stackedImagesThumbs.forEach((img) => {
                    if (`${img.dataset.variantId}` === selectedVariantId || `${img.dataset.variantId}` === `tour`) {
                        img.classList.remove("active");
                        img.classList.remove("visually-hidden"); // show
                        if (!selected) {
                            img.classList.add("active");
                            selected = true;
                        }
                    } else {
                        img.classList.add("visually-hidden"); // hide
                    }
                });
            }

            stackedImagesThumbs.forEach((img, i) => {
                if (i === 0) {
                    img.classList.add("active");
                }
                img.addEventListener('click', () => {
                    // Remove active class on all thumbs
                    stackedImagesThumbs.forEach((thumb) => {
                        thumb.classList.remove("active");
                    });
                    // Add active class to selected thumb
                    img.classList.add("active");

                    window.scrollTo({
                        top: getPosition(document.querySelector(`#${img.dataset.target}`)),
                        left: 100,
                        behavior: 'smooth'
                    });
                });
            });
        } catch (e) {
            console.error(e);
        }
    };

    const handleStickyWishlist = () => {
        try {
            const wishlistContainer = document.querySelector(".wishlist__container");

            if (!wishlistContainer) {
                return;
            }

            // Shared Values
            const screenWidth = window.innerWidth;
            const iconSize = 42;
            const iconPosition = 20;
            const navbarHeight = 75;
            // Desktop Values
            const desktopGallery = document.querySelector('.pdp__gallery');
            const desktopTopLimit = desktopGallery.getBoundingClientRect().top - navbarHeight;
            const desktopBottomLimit = desktopGallery.getBoundingClientRect().bottom - (navbarHeight * 2);
            const desktopRightLimit = desktopGallery.getBoundingClientRect().right;

            // Mobile Values
            const mobileGallery = document.querySelector('.pdp__image-slider.mobile > div');
            const mobileTopLimit = mobileGallery.getBoundingClientRect().top;
            const mobileBottomLimit = mobileGallery.getBoundingClientRect().bottom;
            const mobileRightLimit = mobileGallery.getBoundingClientRect().width;

            if (screenWidth >= 1025) {
                // Is Desktop
                // heightOffset = desktopOffset;
                if (desktopTopLimit <= 0 && desktopBottomLimit >= 0) {
                    // Is Fixed
                    wishlistContainer.style.position = `fixed`;
                    wishlistContainer.style.top = navbarHeight + iconPosition + `px`;
                    wishlistContainer.style.left = desktopRightLimit - iconSize - iconPosition + `px`;
                } else {
                    // Too low
                    wishlistContainer.style.position = `absolute`;
                    wishlistContainer.style.top = iconPosition + `px`;
                    wishlistContainer.style.left = `unset`;
                }
            } else if ((mobileTopLimit - iconSize) <= 0 && (mobileBottomLimit - navbarHeight - iconSize) >= 0) {
                // Is Mobile
                // Is Fixed
                wishlistContainer.style.position = `fixed`;
                const gap = (screenWidth > 767 ? 20 : 0);
                wishlistContainer.style.top = (navbarHeight + gap) + `px`;
            } else if ((mobileTopLimit - iconSize) <= 0) {
                // Too low
                wishlistContainer.style.position = `absolute`;
            } else {
                // Is Initial
                wishlistContainer.style.position = `fixed`;
                wishlistContainer.style.top = mobileTopLimit + iconSize + `px`;
                wishlistContainer.style.left = mobileRightLimit - iconSize - iconPosition + `px`;
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleAutoplayVideos = () => {
        try {
            document.querySelectorAll("[data-product-video]").forEach((video) => {
                if (!video.paused) {
                    const rect = video.getBoundingClientRect();
                    const invisible = rect.x === 0 && rect.y === 0 && rect.width === 0 && rect.height === 0;
                    if (invisible || rect.y < -200) {
                        video.pause();
                        video.classList.remove('playing');
                    }
                }
            });
        } catch (e) {
            console.error(e);
        }
    };

    const handleModalThumbsMobile = () => {
        try {
            document.querySelector('.pdp__image-modal-slider-thumbs').style.top = (window.innerHeight - 68 - 15) + 'px'
        } catch (e) {
            console.error(e);
        }
    };

    const removeLotIdFromUrl = () => {
        try {
            const current = getCurrentUrlAsObject();
            updateUrl(current.lotModalVariantId, null);
        } catch (e) {
            console.error(e);
        }
    };

    const copyUrlToClipboard = () => {
        try {
            const url = window.location.href;
            if (navigator.share) {
                navigator.share({
                    title: 'ArtisticTile',
                    url: url,
                })
                    .then(() => console.log('Successful share'))
                    .catch((error) => console.log('Error sharing', error));
            } else {
                const tempInput = document.createElement("input");
                tempInput.value = url;
                document.body.appendChild(tempInput);
                tempInput.select();
                tempInput.setSelectionRange(0, 99999);
                navigator.clipboard.writeText(tempInput.value);
                document.body.removeChild(tempInput);

                dom.copyToCLipMessage.classList.add("is-visible");
                setTimeout(() => {
                    dom.copyToCLipMessage.classList.remove("is-visible");
                }, 2000);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const updateUrl = (variantId, lotId) => {
        try {
            const current = getCurrentUrlAsObject();
            const obj = {
                ...current,
                lotModalVariantId: variantId,
                lotModalLotId: lotId ? encodeURIComponent(lotId) : null,
            };
            const str = Object.keys(obj).reduce((acc, key) => {
                if (obj[key]) {
                    acc.push(`${key}=${obj[key]}`);
                }
                return acc;
            }, []);
            const url = `?${str.join("&")}`;
            if (!(new URL(document.location)).searchParams.get("customer_posted")) {
                window.history.replaceState({}, "", url);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const getCurrentUrlAsObject = () => {
        try {
            const url = window.location.search;
            const params = new URLSearchParams(url);
            const obj = {};
            for (const [key, value] of params) {
                obj[key] = decodeURIComponent(value);
            }
            return obj;
        } catch (e) {
            console.error(e);
        }
        return {};
    };

    /**
     * Initialize lot inventory gallery
     * @param overrideIsSlab
     */
    const initializeLotModalSlider = (overrideIsSlab = false) => {
        try {
            if (overrideIsSlab) {
                isSlab = true;
            }
            if (isSlab) {
                // lot gallery slider
                if (!$lotSlabSlider) {
                    $lotSlabSlider = $(selectors.lotSlabSlider);
                }

                // init slider
                $lotSlabSlider.not(".slick-initialized").slick(LOT_GALLERY_OPTIONS);

                // init zoom functionality
                initLotZoomSlides();

                // listener for changing data
                $lotSlabSlider.on("beforeChange", (e, slick, currentSlide, nextSlide) => {
                    const targetSlide = slick.$slides[nextSlide];
                    if (
                        targetSlide &&
                        targetSlide.closest("[data-lot-variant-wrapper]").style.display !==
                        "none"
                    ) {
                        const lotSerial = targetSlide.dataset.lotSerial;
                        // const lotQty = targetSlide.dataset.lotQty;
                        const lotSize = targetSlide.dataset.lotSize;
                        const variantId = targetSlide.dataset.variantId;

                        updateUrl(variantId, lotSerial);
                        const updateLotSerialEl = targetSlide
                            .closest("[data-lot-variant-wrapper]")
                            .querySelector(selectors.updateLotSerial);
                        // const updateLotQtyEl = targetSlide.closest("[data-lot-variant-wrapper]").querySelector(selectors.updateLotQty);
                        const updateLotSizeEl = targetSlide
                            .closest("[data-lot-variant-wrapper]")
                            .querySelector(selectors.updateLotSize);

                        updateLotSerialEl.innerText = `Lot ${lotSerial}`;
                        updateLotSizeEl.innerText = `${lotSize}`;
                    }
                });
            }
            if (isTile) {
                // lot gallery slider
                if (!$lotTilesGridSlider) {
                    $lotTilesGridSlider = $(selectors.lotTilesGridSlider);
                }

                // init slider
                $lotTilesGridSlider.not(".slick-initialized").slick(LOT_GALLERY_OPTIONS);
                $(selectors.lotTilesSlider)
                    .not(".slick-initialized")
                    .slick(GRID_GALLERY_OPTIONS);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const updateLotModalProductSliderPosition = (lotId, productType = "slab") => {
        try {
            const targetSlide = document.querySelector(
                `[data-lot-${productType}-slide="${lotId}"`
            );
            if (targetSlide) {
                const index = targetSlide.dataset.slickIndex;
                $lotSlabSlider.slick("goTo", index, true); // don't animate
            }
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Assign productData (must be within a function to avoid JSON parse error on other templates)
     */
    const assignProductData = () => {
        try {
            productData = JSON.parse($(selectors.productJson).text());
            productMetafieldsData = JSON.parse($(selectors.productMetafieldsJson).text());
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Adds inventory_quantity data to variants, since its only available in Liquid by default
     * @returns {{}}
     */
    const mapVariantQuantities = () => {
        try {
            const qtyMap = {};
            if (window.variantQuantities) {
                const quantities = window.variantQuantities.split("|");
                quantities.forEach((str) => {
                    if (str.length) {
                        const pair = str.split("#");
                        qtyMap[pair[0]] = Number(pair[1]);
                    }
                });
            }
            return qtyMap;
        } catch (e) {
            console.error(e);
        }
        return {};
    };

    /**
     * Adds inventory_quantity data to sample variants, since its only available in Liquid by default
     * @returns {{}}
     */
    const mapSampleAvailabilityData = () => {
        try {
            const sampleAvailabilityMap = {};
            if (window.sampleData) {
                const samples = window.sampleData.split("|");
                samples.forEach((str) => {
                    if (str.length) {
                        const firstSplit = str.split("$$");
                        const sku = firstSplit[0];
                        const sampleDataSplit = firstSplit[1].split("#");
                        if (!sampleAvailabilityMap.hasOwnProperty(sku)) {
                            sampleAvailabilityMap[sku] = {
                                id: sampleDataSplit[0],
                                available: sampleDataSplit[1] === "true",
                                price: sampleDataSplit[2] || 0,
                            };
                        }
                    }
                });
            }
            return sampleAvailabilityMap;
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Mimic shopify IMG_URL param for smaller swatches
     * @param src
     * @param size
     * @returns {*|string}
     */
    const imgURL = (src, size) => {
        try {
            // remove any current image size then add the new image size
            return src
                .replace(
                    /_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g,
                    "."
                )
                .replace(/\.jpg|\.png|\.gif|\.jpeg/g, function (match) {
                    return "_" + size + match;
                });
        } catch (e) {
            console.error(e);
        }
        return "";
    };

    /**
     * Set up swatch images using the product images + matching on variant color
     */
    const setupSwatchImages = () => {
        try {
            const swatches = document.querySelectorAll(selectors.swatch);
            swatches.forEach((swatch) => {
                const color = swatch.dataset.color;
                const exampleVariant = productData.variants.find((variant) =>
                    variant.options.includes(color)
                );
                const productFullName =
                    exampleVariant && exampleVariant.metafields.product_full_name
                        ? exampleVariant.metafields.product_full_name.toLowerCase()
                        : "";
                // if it's a molding product, zoom in on the swatch to get a clear color
                if (
                    exampleVariant.featured_image &&
                    productFullName.includes("molding") &&
                    !productFullName.includes("metal")
                ) {
                    // exclude metallic molding because its not centered for the swatch
                    // TODO: have this pull from a list of product / variant IDs instead of doing it procedurally, since this might not apply to all moldings
                    swatch.style.backgroundImage = `url(${imgURL(
                        exampleVariant.metafields.product_photo_1_link,
                        "1600x"
                    )})`;
                    swatch.style.backgroundSize = "auto"; // set to "zoom in" the swatch image on molding products
                } else if (exampleVariant.metafields.product_photo_1_link) {
                    swatch.style.backgroundImage = `url(${imgURL(
                        exampleVariant.metafields.product_photo_1_link,
                        "80x"
                    )})`;
                } else if (exampleVariant.featured_image) {
                    swatch.style.backgroundImage = `url(${imgURL(
                        exampleVariant.featured_image.src,
                        "80x"
                    )})`;
                }
            });
        } catch (e) {
            console.error(e);
        }
    };

    const handleVariantSelect = (optionGroup, optionNum, optionValue) => {
        try {
            clearErrors();

            // trigger a change on Sq Ft input, to make sure QTY errors still appear
            const inputEvent = new Event("input", {
                bubbles: true,
                cancelable: true,
            });
            if (dom.sqFtInput.length > 0) {
                dom.sqFtInput[0].dispatchEvent(inputEvent);
            }

            // get matching variant
            const matchingVariants = productData.variants.filter(
                (variant) =>
                    variant.options[optionNum] ===
                    optionValue.replace(/\$/g, '"').replace(/#/g, "'")
            );
            const variantToSelect = findMatchingVariant(optionNum, matchingVariants);

            // select variant on hidden dropdown
            document.querySelector(selectors.hiddenSelect).value = String(
                variantToSelect.id
            );
            // update code to match
            currentVariant = productData.variants.find(
                (variant) => variant.id === variantToSelect.id
            );

            // check metafield to determine closeout pricing state
            hasCloseoutPrice = !!currentVariant.metafields['Closeout-Discount'];
            closeoutDiscount = parseFloat(`${currentVariant.metafields['Closeout-Discount'] ?? 0}`);
            closeoutInvPerct = (100 - closeoutDiscount) / 100;

            // update the page data
            updatePageData();
            updateGroutData();

            // update image slider
            filterGallerySliders();
            filterStackedGallery();
            handleStickyWishlist();

            // update the button states
            updateOptionButtons(optionGroup, optionNum);

            // update sample button
            sampleVariantData = currentVariant.sample_data;
            updateSampleButton();

            // update slab inventory
            updateSlabInventory();
        } catch (e) {
            console.error(e);
        }
    };

    const initGroupedProducts = () => {
        try {
            const client = new StorefrontClient({
                shop: 'artistictile',
                accessToken: '63253b5eb4e4c3afcce873b0c560a708'
            });
            const createGroupedItemsSlider = (nodes) => {
                const groupedItemsSlider = $(".grouped-items_slider");
                const groupedItem = (item) => {
                    return `<div class="grouped-items_slider_inner">
          <div class="grouped-items_slider_inner_hover">
            <div class="grouped-items_img">
              <img src="${item.image.url}">
            </div> 
            <div class="grouped-items_title">  
              ${item.product.title + editTitle(item.title.replace(/\-/g, " "))}
            </div>
            <a class="grouped-items_button button" href="${item.product.handle}?variant=${item.id.split('ProductVariant/')[1]}">Learn More</a>
          </div>
        </div>`;
                }
                const editTitle = (title) => {
                    return title.split('" /')[1];
                }
                if (groupedItemsSlider.hasClass('slick-initialized')) groupedItemsSlider.slick('unslick');
                groupedItemsSlider.empty();
                nodes.forEach(item => groupedItemsSlider.append(groupedItem(item)))
                groupedItemsSlider.slick({
                    dots: false,
                    infinite: false,
                    nextArrow:
                        '<svg class="grouped-items_slider_arrow_right" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="8px" height="16px" viewBox="0 0 8 16" version="1.1"> <title>Path</title> <g id="Related-Products" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"> <g id="D---PDP---Related-Products" transform="translate(-1391.000000, -646.000000)" fill="#494742"> <g id="In-This-Collection" transform="translate(118.000000, 437.000000)"> <g id="Arrow" transform="translate(1258.000000, 198.000000)"> <g id="noun_Arrow_2335652" transform="translate(19.000000, 18.776119) rotate(-90.000000) translate(-19.000000, -18.776119) translate(11.000000, 14.776119)"> <polygon id="Path" points="7.99052632 8 15.78 0.311688312 15.4852632 0 7.99052632 7.3974026 0.495789474 0 0.18 0.311688312"/> </g> </g> </g> </g> </g> </svg>',
                    prevArrow:
                        '<svg class="grouped-items_slider_arrow_left" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="8px" height="16px" viewBox="0 0 8 16" version="1.1"> <title>Path</title> <g id="Related-Products" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"> <g id="D---PDP---Related-Products" transform="translate(-132.000000, -646.000000)" fill="#494742"> <g id="In-This-Collection" transform="translate(118.000000, 437.000000)"> <g id="Arrow" transform="translate(18.500000, 217.000000) scale(-1, 1) translate(-18.500000, -217.000000) translate(0.000000, 198.000000)"> <g id="noun_Arrow_2335652" transform="translate(19.000000, 18.776119) rotate(-90.000000) translate(-19.000000, -18.776119) translate(11.000000, 14.776119)"> <polygon id="Path" points="7.99052632 8 15.78 0.311688312 15.4852632 0 7.99052632 7.3974026 0.495789474 0 0.18 0.311688312"/> </g> </g> </g> </g> </g> </svg>',
                    slidesToShow: 3,
                    slidesToScroll: 3,
                    responsive: [
                        {
                            breakpoint: 768,
                            settings: {
                                arrows: false,
                                slidesToShow: 2,
                                slidesToScroll: 2,
                            },
                        },
                    ],
                });
            }
            const fetchProducts = async (query) => {
                try {
                    const {data: {nodes}} = await client.fetch({
                        query: `query($ids: [ID!]!) {
            nodes(ids: $ids) {
              ... on ProductVariant {
                id
                title
                image {
                  url
                }
                product {
                  handle
                  title
                }
                metafield(namespace: "global", key: "short_description") {
                  value
                }
              }
            }
          }`,
                        variables: {
                            ids: query
                        }
                    })
                    const validResults = nodes.filter(el => el);
                    if (validResults.length > 0) {
                        createGroupedItemsSlider(validResults)
                    }
                } catch (error) {
                    console.error(error)
                }
            }
            if (currentVariant.metafields['Group-item-codes']) {
                const ids = currentVariant.metafields['Group-item-codes'].split(', ').map(code => {
                    return btoa(`gid://shopify/ProductVariant/${code}`)
                })
                fetchProducts(ids);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleVariantSlide = (wrapper, direction) => {
        try {
            if (optionSliderAnimating) return;
            // using jQuery animate to polyfill smooth scroll on safari
            optionSliderAnimating = true;
            let scrollAmount =
                wrapper.querySelector(selectors.optionButton).offsetWidth - 1;
            const currentScrollPosition = $(wrapper).scrollLeft();

            scrollAmount = direction === "forward" ? scrollAmount * 1 : scrollAmount * -1;
            $(wrapper).animate(
                {scrollLeft: currentScrollPosition + scrollAmount},
                250,
                "linear",
                () => {
                    // TODO: snap to start/end if past a certain threshold (lots of off-by-one due to the margin hack)
                    optionSliderAnimating = false;
                }
            );
        } catch (e) {
            console.error(e);
        }
        // $(wrapper).mouseenter().mouseleave(); // trigger scroll snap
    };

    /**
     * Algorithm to determine which variant data to apply to option states
     * @param selectedOptionNum
     * @param matchingVariants
     * @returns {*|null}
     */
    const findMatchingVariant = (selectedOptionNum, matchingVariants) => {
        try {
            let matchedVariant = matchingVariants[0];
            // if there's only one match, return it
            if (matchingVariants.length === 1) {
                return matchedVariant;
            }
            // set up an object to track variants' shared options among those the user hasn't changed
            const sharedOptionsByVariant = {};
            // iterate over the other product options
            productData.options
                .map((option, index) => {
                    return {option: option, index: index};
                })
                .filter((option) => option.index != selectedOptionNum)
                .forEach((otherOption) => {
                    const currentOptionValue = currentVariant.options[otherOption.index];
                    matchingVariants.forEach((variant) => {
                        if (variant.options[otherOption.index] === currentOptionValue) {
                            // current option value works with new combo; increment shared option counter
                            sharedOptionsByVariant[variant.id]
                                ? sharedOptionsByVariant[variant.id]++
                                : (sharedOptionsByVariant[variant.id] = 1);
                        }
                    });
                });

            // select the variant with the most shared options (if applicable)
            // otherwise use the original matched variant
            const entries = Object.entries(sharedOptionsByVariant);
            const nonZeroSharedEntries = entries.filter((pair) => pair[1] > 0);

            if (!nonZeroSharedEntries.length) {
                return matchedVariant;
            } else if (nonZeroSharedEntries.length === 1) {
                // if only one variant shares option, use that as matched variant
                matchedVariant = matchingVariants.find(
                    (variant) => variant.id == nonZeroSharedEntries[0][0]
                );
            } else {
                // if there are multiple variants with shared options,
                // sort them to find the matching variant with the most shared options
                const bestMatchId = nonZeroSharedEntries.sort((a, b) =>
                    b[1] > a[1] ? 1 : -1
                )[0][0];
                matchedVariant = matchingVariants.find(
                    (variant) => variant.id === Number(bestMatchId)
                );
            }

            return matchedVariant;
        } catch (e) {
            console.error(e);
        }
        return null;
    };

    const setAvailableStockMessaging = () => {
        try {
            let markup;
            if (currentVariant.metafields['Coming-Soon'] === 'Yes') {
                markup = `Coming Soon`;
            } else {
                if (currentVariant.available) {
                    let inventoryQuantity = currentVariant.inventory_quantity;
                    markup = `IN STOCK (${convertStockByUnit(inventoryQuantity)} available)`;
                } else {
                    const restockDate = currentVariant.metafields.stock_available_date;
                    markup = `Out Of Stock ${restockDate ? ` - Restock ETA: ${restockDate}` : ""}`;
                }
            }
            dom.stockMessage.innerHTML = markup;
        } catch (e) {
            console.error(e);
        }
    };

    const setAvailableStockForSlabs = (force = false) => {
        try {
            let markup = "";
            if (currentVariant.available || force) {
                if (currentVariant.metafields.lot_data) {
                    const locationsQtyCollector = [];
                    const lotRows = currentVariant.metafields.lot_data
                        .replace(/\(/g, "##")
                        .replace(/\)/g, "")
                        .replace(/\[/g, "##")
                        .replace(/\]/g, "")
                        .split(",");
                    lotRows.forEach((lotRow, index) => {
                        const lotProps = theme.artisticTile.extractLotProperties(lotRow, index, []);
                        if (parseFloat(lotProps.lotQuantity) > 0) {
                            const i = locationsQtyCollector.findIndex(item => item.location === lotProps.lotLocation)
                            if (i === -1) {
                                locationsQtyCollector.push({
                                    location: lotProps.lotLocation,
                                    qty: 1
                                });
                            } else {
                                locationsQtyCollector[i].qty += 1
                            }
                        }
                    });
                    locationsQtyCollector.forEach((lqc) => {
                        markup += `${lqc.qty} available in ${lqc.location}<br/>`;
                    });
                }
            }
            dom.stockSlabMessage.innerHTML = markup;
        } catch (e) {
            console.error(e);
        }
    };

    const updatePrice = (price, doms, boldify = false) => {
        try {
            price = price.toFixed(2);
            const p = document.querySelector('.pdp__price-container');
            //
            doms.forEach((el) => {
                el.textContent = `${price}`;
                if (price > 0) {
                    if (el.parentNode) {
                        toggleVisibility(el.parentNode, true);
                    }
                    if (boldify) {
                        p.classList.add('bold');
                    }
                } else {
                    if (el.parentNode) {
                        toggleVisibility(el.parentNode, false);
                    }
                    if (boldify) {
                        p.classList.remove('bold');
                    }
                }
            });
        } catch (e) {
            console.error(e);
        }
    }

    /**
     * Update variant data on the page - including calculator content behind the scenes
     */
    const updatePageData = () => {
        try {
            // update selected variant values
            dom.optionGroup.forEach((group, index) => {
                const optionNum = group.dataset.optionNum;
                const selectedOptionSpan = group.querySelector(selectors.selectedOption);
                selectedOptionSpan.textContent = currentVariant.options[optionNum].replace(/\-/g, " ");
            });

            // BUY BOX CONTENT
            // this might come into play later (each vs. sqft)
            let unitOfMeasure = theme.artisticTile.translateUnitMeasure(currentVariant.metafields.unit_measure);
            if (`${unitOfMeasure}` === `Each`) {
                dom.priceUnitType.forEach((el) => (el.textContent = "each"));
            } else if (`${unitOfMeasure}` === `SF`) {
                dom.priceUnitType.forEach((el) => (el.textContent = "per S/F"));
            }

            if (currentVariant.metafields.product_name) {
                dom.productTitle.forEach(title => {
                    title.textContent = currentVariant.metafields.product_name.replace(/\-/g, " ")
                })
            }

            // VIEW ALL LOTS
            document.querySelector('.pdp__cta-links a').dataset.viewLotsBtn = currentVariant.id;

            // show missing data error for client
            dom.productSubtitle.forEach(subTitle => {
                subTitle.textContent = currentVariant.metafields.short_description ?
                    currentVariant.metafields.short_description.replace(/\-/g, " ") : "";
            });

            // sku
            dom.sku.textContent = currentVariant.sku;
            if (!currentVariant.available && dom.productionLeadTime) {
                dom.productionLeadTime.textContent = currentVariant.metafields.Production_Lead_Time;
            } else if (dom.productionLeadTime) {
                dom.productionLeadTime.textContent = "";
            }

            calculateAndRenderPricing();

            updateDiscountMessaging();

            // DETAILS
            updateDetails();

            // LOT QUANTITY THRESHOLD
            mapThresholdQuantity();

            // update URL for current variant
            let originalUrl = new URL(window.location.href);
            let originalParams = new URLSearchParams(originalUrl.search);
            originalParams.set("variant", currentVariant.id);

            if (!originalUrl.searchParams.get("customer_posted")) {
                const updatedUrl = `${window.location.href.split("?")[0]
                }?${originalParams.toString()}`;
                window.history.replaceState({}, "", updatedUrl);
            }

            // skip calculator updates for inquire-state products (slab / tailored to) and 'each' products
            if (!inquireState && unitOfMeasure !== "Each") {
                // CALCULATOR CONTENT
                // update variables needed in the calculator, based on variant data
                soldByFullBox = currentVariant.metafields.sold_full_box !== "F";
                pricePerSqFt = Number(currentVariant.metafields.price_per_sq_ft).toFixed(2) || "N/A";
                sqFtPerPiece = Number(currentVariant.metafields.sq_ft_per_piece);
                sqFtPerBox = Number(currentVariant.metafields.sq_ft_per_box);
                sqFtPerUnit = soldByFullBox ? sqFtPerBox : sqFtPerPiece;
                // recommended overage - comes in with % as a string, so clean up
                overagePercentage = currentVariant.metafields.recommended_overage
                    ? Number(currentVariant.metafields.recommended_overage.replace("%", "").trim())
                    : 10;
                dom.pricePerSqFt.forEach((el) => (el.textContent = pricePerSqFt)); // update price at the top of the calculator

                // dom.sqFtPerUnit.textContent = sqFtPerUnit;
                dom.overagePercentage.forEach(
                    (span) => (span.textContent = String(overagePercentage))
                );
                // END CALCULATOR CONTENT
            }

            // OUT OF STOCK UPDATES
            const isOOO = !currentVariant.available && !inquireState && !madeInAmerica;
            const comingSoon = currentVariant.metafields['Coming-Soon'] === 'Yes';
            if (isOOO || comingSoon) {
                // only update stock-related elements for non-inquire products
                // disable ATC + update text
                dom.addToCart.disabled = true;
                dom.addToCart.textContent = comingSoon ? "Coming Soon" : "Out of Stock";
                if (dom.slabOutOfStockMessage) {
                    dom.slabOutOfStockMessage.textContent = comingSoon ?
                        "Please inquire to be alerted when stock becomes available." :
                        "This item is currently sold out. Please inquire below for next availability.";
                }

                // hide "Calculate button" and show "Notify Me" button
                if (unitOfMeasure !== "Each") {
                    // square foot products
                    toggleVisibility(dom.sqFtNeededContainer, false, 'visually-');
                } else {
                    // each product
                    toggleVisibility(dom.eachAddToCart, false, 'visually-');
                }

                toggleVisibility(dom.notifyMe, true, 'visually-');
                dom.notifyMe.style.display = "block"; // overrides Klaviyo script's default functionality

                // showInquireButton();
                showInputError(true);

                // only display the message in the calculator
                if (unitOfMeasure === "SF") {
                    showATCMessage(
                        `This item is ${comingSoon ? "coming soon" : "currently out of stock"}. <a href="#" data-inquire-link>Contact us</a> for more information.`
                    );
                }

                // if it's an "each" product, hide the quantity selector
                if (dom.inlineQuantity) dom.inlineQuantity.classList.add("hidden");

                // set the stock messaging
                setAvailableStockMessaging();

                // style the stock message red when out of stock
                dom.stockMessage.classList.add("oos");
            } else if (currentVariant.available && !inquireState && !madeInAmerica) {
                // only update stock-related items for non-inquire products
                // enable ATC + update text
                dom.addToCart.disabled = false;
                dom.addToCart.textContent = "Add To Cart";

                // hide "Notify Me" button
                toggleVisibility(dom.notifyMe, false, 'visually-');
                // show "Calculate button"
                if (unitOfMeasure !== "Each") {
                    // square foot products
                    toggleVisibility(dom.sqFtNeededContainer, true, 'visually-');
                } else {
                    // each products
                    toggleVisibility(dom.eachAddToCart, true, 'visually-');
                }

                // if its an "each" product, show the quantity selector
                if (dom.inlineQuantity) dom.inlineQuantity.classList.remove("hidden");

                // set the stock messaging
                setAvailableStockMessaging();

                // remove out of stock styling on SKU
                dom.stockMessage.classList.remove("oos");

                removeATCMsg(selectors.ATCInquireMessage);
            } else if (inquireState) {
                // set the stock messaging
                setAvailableStockForSlabs();
            }

            // show/hide the "sold in full boxes" callout
            const boxCallout = document.querySelector(".pdp__sold-by-box");
            if (!soldByFullBox && boxCallout) {
                boxCallout.style.display = "none";
            } else if (soldByFullBox && !boxCallout) {
                const message = document.createElement("p");
                message.textContent = "Sold in Full Boxes";
                message.classList.add("pdp__sold-by-box");
                document.querySelector(".pdp__info-box").appendChild(message);
            }

            // SELECTED LOT
            cleanSelectedLot();
            reCalculate();

            // WAIT FOR 2.5s THEN PLAY VIDEOS, AUTOPLAY WILL NOT WORK ON STACKED
            setTimeout(() => {
                async function playVideo(video) {
                    try {
                        await video.play();
                        video.classList.add("playing");
                    } catch (err) {
                        console.log(err);
                    }
                }

                document.querySelectorAll("[data-product-video]").forEach((vid) => {
                    playVideo(vid);
                });
            }, 2500);
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Update recommended grout data on the page
     */
    const updateGroutData = () => {
        try {
            const productSku = currentVariant.metafields.recommended_grout_color;
            if (productSku) {
                toggleVisibility(document.querySelector(selectors.dataGrout), true, 'visually-');
                $.get(`/search?type=product&q=variants.sku:${productSku}&view=sku`).done((response) => {
                    // add grout data
                    const product = JSON.parse(response).products[0];
                    if (product) {
                        let productTitle = '';
                        let productUrl = '';
                        for (const variant of product.variants) {
                            if (variant.sku === productSku) {
                                productTitle = variant.title;
                                productUrl = `https://artistictile.com/products/${product.handle}?variant=${variant.id}`;
                                break;
                            }
                        }
                        document.querySelector(`${selectors.dataGrout} .value`).innerHTML = `<a target="_blank" href="${productUrl}">${productTitle}</a>`;
                    } else {
                        toggleVisibility(document.querySelector(selectors.dataGrout), false, 'visually-');
                    }
                })
            } else {
                toggleVisibility(document.querySelector(selectors.dataGrout), false, 'visually-');
            }
        } catch (e) {
            console.error(e);
        }
    }

    const showCalculator = () => {
        try {
            const calculatorPrompt = document.querySelector(".pdp__calculation-prompt");
            const buyBox = document.querySelector(".pdp__buy-box");
            if (calculatorPrompt && buyBox) {
                setTimeout(() => {
                    buyBox.style.position = 'initial';
                    window.scrollTo({
                        top: getPosition(calculatorPrompt)[0],
                        behavior: 'smooth'
                    });
                    const input = calculatorPrompt.querySelector('.sqft-input-wrapper input');
                    if (input) {
                        input.focus();
                    }
                    window.addEventListener('scroll', () => {
                        const rect = calculatorPrompt.getBoundingClientRect();
                        if ((rect.top + rect.height) < 250) {
                            buyBox.style.position = '';
                        }
                    });
                }, 800)
            }
        } catch (e) {
            console.error(e);
        }
    };

    const hideCalculator = () => {
        try {
            document.body.classList.remove("calculator-open");
            closeTooltip();
        } catch (e) {
            console.error(e);
        }
    };

    const toggleVisibility = (element, visible, prefix = '') => {
        element.setAttribute('aria-hidden', visible ? "false" : "true");
        if (visible) {
            element.classList.remove(`${prefix}hidden`);
        } else {
            element.classList.add(`${prefix}hidden`);
        }
    }

    const toggleFormula = () => {
        try {
            if (showFormula) {
                toggleVisibility(dom.sqFtNeededContainer, true, 'visually-');
                toggleVisibility(dom.sqFtNeededContainerFormula, false, 'visually-');
                dom.showFormula.innerText = 'Calculate by dimensions';
            } else {
                toggleVisibility(dom.sqFtNeededContainer, false, 'visually-');
                toggleVisibility(dom.sqFtNeededContainerFormula, true, 'visually-');
                dom.showFormula.innerText = 'Calculate by s/f';
            }
            showFormula = !showFormula;
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Update discount messaging based on trade status + clearance product status
     */
    const updateDiscountMessaging = () => {
        try {
            let message = "";
            if (hasCloseoutPrice) {
                message += `${closeoutDiscount}% Clearance`;
                if (discountPercentage) {
                    message += ` + ${discountPercentage}% Trade Discount`;
                }
            } else if (discountPercentage) {
                message += `${discountPercentage}% Trade Discount`;
            }

            if (hasCloseoutPrice || discountPercentage) {
                dom.discountMessage.textContent = message;
                dom.discountMessage.parentElement.style.display = "inline-block";
            } else {
                dom.discountMessage.parentElement.style.display = "none";
                dom.discountMessage.textContent = "";
            }
        } catch (e) {
            console.error(e);
        }
    };

    /**
     *
     * @param basePrice
     * @param basePricePerBox
     * @param render
     * @returns {{discountPricePerBox: number, discountPrice: (string|*|number)}}
     */
    const calculateCloseoutAndTradeDiscounts = (
        basePrice,
        basePricePerBox,
        render = true
    ) => {
        // calculate discount for trade members
        let discountPrice = discountPercentage
            ? Number((basePrice * (100 - discountPercentage)) / 100)
            : (hasCloseoutPrice ? basePrice : 0);

        // per box
        let discountPricePerBox = 0;
        if (basePricePerBox) {
            discountPricePerBox = discountPercentage
                ? Number((basePricePerBox * (100 - discountPercentage)) / 100)
                : (hasCloseoutPrice ? basePricePerBox : 0);
        }

        if (isTradeMember && !hasCloseoutPrice) {
            discountPrice = Number((basePrice * (100 - discountPercentage)) / 100);
            if (render) {
                updatePrice(discountPrice, dom.discountPrice);
                updatePrice(basePrice, dom.price);
            }

            // per box
            if (basePricePerBox) {
                discountPricePerBox = Number((basePricePerBox * (100 - discountPercentage)) / 100);
                if (render) {
                    updatePrice(discountPricePerBox, dom.discountPricePerBox);
                    updatePrice(basePricePerBox, dom.pricePerBox, true);
                }
            }
        } else if (isTradeMember && hasCloseoutPrice) {
            if (`${unitOfMeasure}` === `SF`) {
                // apply the extra closeout discount to the displayed price
                discountPrice = Number(((basePrice * (100 - discountPercentage)) / 100) * closeoutInvPerct);
                if (render) {
                    updatePrice(discountPrice, dom.discountPrice);
                    updatePrice(basePrice, dom.price);
                }

                // per box
                if (basePricePerBox) {
                    discountPricePerBox = Number(((basePricePerBox * (100 - discountPercentage)) / 100) * closeoutInvPerct)
                    if (render) {
                        updatePrice(discountPricePerBox, dom.discountPricePerBox);
                        updatePrice(basePricePerBox, dom.pricePerBox, true);
                    }
                }
            } else {
                // regular discount on 'each' products, since price already pre-discounted
                discountPrice = Number((basePrice * (100 - discountPercentage)) / 100);
                if (render) {
                    updatePrice(discountPrice, dom.discountPrice);
                    updatePrice((basePrice * 1.6667), dom.price);
                }
            }
        } else if (hasCloseoutPrice) {
            // reverse engineered discount for closeout tagged products (it's all for show)
            if (`${unitOfMeasure}` === `SF`) {
                // S/F is not pre-calculated, so calculate discount before displaying on the frontend
                discountPrice = Number(basePrice * closeoutInvPerct);
                if (render) {
                    updatePrice(discountPrice, dom.discountPrice);
                    updatePrice(basePrice, dom.price);
                }

                // per box
                if (basePricePerBox) {
                    discountPricePerBox = Number(basePricePerBox * closeoutInvPerct);
                    if (render) {
                        updatePrice(discountPricePerBox, dom.discountPricePerBox);
                        updatePrice(basePricePerBox, dom.pricePerBox, true);
                    }
                }
            } else {
                if (render) {
                    updatePrice(basePrice, dom.discountPrice);
                    updatePrice((basePrice * 1.6667), dom.price);
                }
            }
        } else {
            if (render) {
                updatePrice(basePrice, dom.price);
                updatePrice(basePricePerBox, dom.pricePerBox, true);
            }
        }

        if (render) {
            // hide / show price container based on discounts
            if (discountPrice !== 0) {
                dom.priceContainer.forEach((el) => toggleVisibility(el, false, 'visually-'));
                dom.compareAtPriceContainer.forEach((el) => toggleVisibility(el, true, 'visually-'));
            } else {
                dom.priceContainer.forEach((el) => toggleVisibility(el, true, 'visually-'));
                dom.compareAtPriceContainer.forEach((el) => toggleVisibility(el, false, 'visually-'));
            }
            // per box
            if (basePricePerBox) {
                if (discountPricePerBox !== 0) {
                    dom.priceContainerPerBox.forEach((el) => toggleVisibility(el, false, 'visually-'));
                    dom.compareAtPriceContainerPerBox.forEach((el) => toggleVisibility(el, true, 'visually-'));
                } else {
                    dom.priceContainerPerBox.forEach((el) => toggleVisibility(el, true, 'visually-'));
                    dom.compareAtPriceContainerPerBox.forEach((el) => toggleVisibility(el, false, 'visually-'));
                }
            }
        }

        return {
            discountPrice: discountPrice !== 0 ? discountPrice : basePrice,
            discountPricePerBox: discountPricePerBox !== 0 ? discountPricePerBox : basePricePerBox
        }
    }

    /**
     *
     */
    const calculateAndRenderPricing = () => {
        try {
            const soldFullBox = currentVariant.metafields.sold_full_box;
            const pricePerSqFt = currentVariant.metafields.price_per_sq_ft;
            const sqFtPerBox = currentVariant.metafields.sq_ft_per_box;

            let newPrice;
            let newPricePerBox = null;
            if (`${unitOfMeasure}` === `SF`) {
                // displays price_per_sq_ft, or a message for missing data
                newPrice = pricePerSqFt ? Number(pricePerSqFt) : null;
                // per box
                if (soldFullBox) {
                    newPricePerBox = pricePerSqFt ? Number(pricePerSqFt * sqFtPerBox) : null;
                }
            } else {
                // displays variant price per unit
                newPrice = Number(currentVariant.price / 100);
                // per box
                if (soldFullBox) {
                    newPricePerBox = Number(currentVariant.pricePerBox / 100);
                }
            }

            calculateCloseoutAndTradeDiscounts(newPrice, newPricePerBox);
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Update all option button states
     * @param optionGroup
     * @param optionNum
     */
    const updateOptionButtons = (
        optionGroup = false,
        optionNum = false
    ) => {
        try {
            if (!dom.optionGroup.length) return;

            // clear all option state classes
            dom.optionButton.forEach((option) => {
                option.classList.remove("active"); // selected
                option.classList.remove("disabled"); // out of stock
                option.classList.remove("unavailable"); // doesn't exist
                option.removeAttribute("title");
            });

            // update active class on selected options
            dom.optionGroup.forEach((group, index) => {
                const selectedOptionValue = currentVariant.options[index]
                    .replace(/\"/g, "$")
                    .replace(/\'/g, "#"); // get selected option value
                group
                    .querySelectorAll(selectors.optionButton)
                    .forEach((button) => button.classList.remove("active")); // clear active class
                const selectedOption = group.querySelector(
                    `[data-option-value="${selectedOptionValue}"]`
                );
                selectedOption.classList.add("active"); // add active class to selected option value

                if (!currentVariant.available) {
                    selectedOption.classList.add("disabled"); // apply OOS state to the selected option
                    selectedOption.title = "This combination is out of stock.";
                }

                // scroll other selection options into view
                if (index !== optionNum) {
                    // scroll out-of-view options into view
                    const selectedOption = group.querySelector(
                        `[data-option-value="${selectedOptionValue}"]`
                    );
                    const scrollContainer = group.querySelector(selectors.optionsWrapper);
                    const scrollToPosition =
                        selectedOption.offsetLeft -
                        (scrollContainer.offsetWidth - selectedOption.offsetWidth) / 2;
                    // using slight delay to prevent glitchy visual on certain variant selections
                    setTimeout(() => {
                        $(scrollContainer).animate(
                            {scrollLeft: scrollToPosition},
                            250,
                            "linear"
                        );
                    }, 150);
                }
            });

            // update unavailable and disabled (out of stock) button states
            if (optionGroup !== false) {
                // not initial page load - update when an option group is selected
                //  get array of variants that share the selected primary option
                //      some storefronts will use a 'primary option' if there is a 'top-down' flow of choices
                //     (ie. choose size, have other option states reflect based on the selected size)
                const samePrimaryOptionVariants = productData.variants.filter(
                    (variant) => currentVariant.options[0] == variant.options[0]
                );

                // get all options groups that aren't primary
                const secondaryOptionGroups = [...dom.optionGroup];
                secondaryOptionGroups.shift(); // remove first item from array

                // for each secondary option group, run through each option button and determine if the matching variants include that value in their options
                secondaryOptionGroups.forEach((group) => {
                    const groupNum = Number(group.dataset.optionNum);
                    group.querySelectorAll(selectors.optionButton).forEach((button) => {
                        const matchedVariants = samePrimaryOptionVariants.filter(
                            (variant) =>
                                variant.options[groupNum]
                                    .replace(/\"/g, "$")
                                    .replace(/\'/g, "#") === button.dataset.optionValue
                        );
                        if (!matchedVariants.length) {
                            // add unavailable state to button
                            button.classList.add("unavailable");
                            button.title = "This combination is not available.";
                        } else {
                            button.classList.remove("unavailable");
                            if (!button.classList.contains("disabled")) {
                                button.removeAttribute("title");
                            }
                        }
                    });
                });

                // update out of stock states
                samePrimaryOptionVariants.forEach((variant) => {
                    // if variants with the same primary option selection aren't available, apply OOS states to non-color buttons
                    if (!variant.available) {
                        secondaryOptionGroups.forEach((group) => {
                            const groupNum = Number(group.dataset.optionNum);
                            const buttonToDisable = Array.from(dom.optionButton).find(
                                (button) =>
                                    variant.options[groupNum] ===
                                    button.dataset.optionValue
                                        .replace(/\$/g, '"')
                                        .replace(/#/g, "'")
                            );
                            if (buttonToDisable) {
                                // add disabled state - out of stock
                                buttonToDisable.classList.add("disabled");
                            } else {
                                console.log(`Couldn't find button for ${variant.options[groupNum]}`);
                            }
                        });
                    }
                });
            }
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Called right before adding to cart, using the most up-to-date data to populate the line item properties
     */
    const updateLineItemPropertyFields = () => {
        try {
            if (unitOfMeasure !== "SF") return; // only using line item properties for Sq Ft products
            const retailUnit = soldByFullBox ? "box" : "piece";
            // odd way to do this. oh well
            dom.prop.forEach((prop) => {
                const propName = prop.dataset.prop;
                switch (propName) {
                    case "sq-ft-per-unit":
                        prop.value = sqFtPerUnit;
                        break;
                    case "retail-unit":
                        prop.value = retailUnit;
                        break;
                    case "sq-ft-lot":
                        if (sqFtChosenLot) {
                            prop.value = sqFtChosenLot.lotSerial;
                        }
                        break;
                    case "multi-lot-confirmation":
                        break;
                    default:
                        console.log(propName, "What happened?");
                }
            });
        } catch (e) {
            console.error(e);
        }
    };

    const debouncedInitialsValidation = debounce((e) => {
        validateInitialsEntry(e.target);
    }, 400);

    const validateInitialsEntry = (initialsInput) => {
        try {
            const initials = initialsInput.value.toUpperCase().trim();

            if (initials.length !== 0 && initials.match(/[A-Za-z]/g)) {
                clearErrors();
                validatedInitials = initials;
            } else {
                // show error state
                initialsInput.parentElement.classList.add("error");
                validatedInitials = undefined;
            }
        } catch (e) {
            console.error(e);
        }
    };

    const addMultiLotToCart = () => {
        try {
            // add input with value of initials
            const existingInitialsInput = document.querySelector(
                '[data-prop="multi-lot-signature"]'
            );
            if (existingInitialsInput) {
                // update initials value for ATC
                existingInitialsInput.value = validatedInitials;
            } else {
                // create initials input for ATC
                // following this format:
                // <input type="hidden" data-prop="multi-lot-signature" name="properties[Multi Lot Signature]" value="{validatedInitials}" />
                const initialsPropInput = document.createElement("input");
                initialsPropInput.setAttribute("type", "hidden");
                initialsPropInput.dataset.prop = "multi-lot-signature";
                initialsPropInput.setAttribute("name", "properties[Multi Lot Signature]");
                initialsPropInput.value = validatedInitials;
                dom.innerForm.appendChild(initialsPropInput);
            }
            submitProductForm();
        } catch (e) {
            console.error(e);
        }
    };

    const submitProductForm = () => {
        try {
            if (wishlistId) {
                dom.productForm.innerHTML += `<input type="hidden" name="return_to" value="/pages/swym-wishlist?wishlist=${wishlistId}" />`;
            }
            dom.productForm.submit();
        } catch (e) {
            console.error(e);
        }
    };

    const prepareInventoryLotRow = (row, index, lotPhotoData, currentVariant) => {
        let tableMarkup = '';
        let lotSelectorBtn = undefined;
        let lotProps = undefined;
        try {
            lotProps = theme.artisticTile.extractLotProperties(row, index, lotPhotoData);

            // custom below the fold measure logic
            const productTag = productData.tags.find(tag => tag.includes('subcategory'));
            const isCustom = productCategories.includes(productTag);
            const productTitle = dom.productTitle[0].textContent.replace(/["']/g, "");

            if (lotProps.lotQuantity === 0 || lotProps.lotStatus === 'N') {
                return undefined;
            }

            // helper
            const printColumn = (css, title, value) => {
                return `<div class="stock-info__${css}">
                    <span class="stock-info__cell-title">${title}</span>
                    <span class="stock-info__cell-content">${value}</span>
                  </div>`;
            }
            const printImg = () => {
                return lotProps.lotImage ?
                    `<a class="JS stock-info__lot-image" tabindex="0" data-pic-index="${lotProps.lotPhotoIndex}" data-open-lot-zoom data-lot-serial="${lotProps.lotSerial}" data-variant-id="${currentVariant.id}">
                        <img src="${lotProps.lotImage}" loading="lazy" alt="${isSlab ? `${productTitle} - ${lotProps.lotSerial} - ${lotProps.lotQuantity}` : `${productTitle}`}">
                        <span class="stock-info__lot-image-zoom-prompt">
                          ${ZOOM_ICON_MARKUP}
                        </span>
                    </a>` :
                    `<a href="mailto:${mailto}?subject=${subject}&body=${body}" class="stock-info__lot-image no-image" tabindex="0" data-open-lot-zoom data-lot-serial="${lotProps.lotSerial}">
                        <span>Request Image</span>
                    </a>`;
            }

            // table markup
            const price = currentVariant.metafields.price_per_sq_ft;
            const priceWithDiscount = calculateCloseoutAndTradeDiscounts(price, null, false);
            const finalPrice = `${priceWithDiscount.discountPrice}` !== `${price}` ? parseFloat(priceWithDiscount.discountPrice) : parseFloat(price);
            lotProps = {
                ...lotProps,
                price: finalPrice,
            };

            const measure = isCustom ? 'EA' : 'S/F';
            const mailto = `customerservicedept@artistictile.com`;
            const subject = `Artistic%20Tile%20Image%20Request`;
            const body = `Hello%2C%20I%E2%80%99d%20like%20to%20request%20an%20image%20of%20LOT%20%23${lotProps.lotSerial}%20for%20${dom.sku.textContent}%20of%20${productTitle}.%20Thank%20you%20in%20advance%21`;
            tableMarkup = `<div class="stock-info__lot-link-row">
                                <div class="stock-info__mobile-left">
                                      ${printColumn('lot-serial', 'Lot Serial', lotProps.lotSerial)}
                                      ${!isCustom ? printColumn('lot-qty', 'Dimensions', lotProps.lotDimensions) : ''}
                                      ${lotProps.lotQuantity ? printColumn('lot-qty', 'Qty Available', `${lotProps.lotQuantity} ${measure}`) : ''}
                                      ${lotProps.lotStatus === "Y" ? printColumn('lot-available', 'Status', `Available`) : ''}
                                      ${lotProps.lotLocation ? printColumn('lot-location', 'Location', lotProps.lotLocation) : ''}
                                      ${finalPrice && isWSDevOnly ? printColumn('lot-qty', 'Lot Price', `$${finalPrice}`) : ''}
                                </div>
                                ${!isCustom ? printImg() : ''}
                           </div>`;

            // lot selector markup
            lotSelectorBtn = document.createElement('button');
            lotSelectorBtn.type = 'button';
            lotSelectorBtn.className = 'grid grid-cols-3 hover:font-bold';
            lotSelectorBtn.dataset.lotSerial = `${lotProps.lotSerial}`;

            lotSelectorBtn.innerHTML = `<span>${lotProps.lotSerial}</span><span>${lotProps.lotQuantity} S/F</span><span>$${finalPrice}</span>`;
            lotSelectorBtn.addEventListener('click', () => {
                sqFtLotSelectorBtns.map(btn => btn.classList.remove('selected'));
                sqFtSelectedLot = lotProps;
                lotSelectorBtn.classList.add('selected');
                dom.lotChoose.disabled = false;
            });
        } catch (e) {
            console.error(e);
        }
        return [tableMarkup, lotSelectorBtn, lotProps];
    }

    const updateDetails = () => {
        try {
            if (metafieldSource === 'variant') {
                // only init grouped items until productData has loaded
                initGroupedProducts();
            }

            dom.variableDetail.forEach((el) => {
                const field = `${el.dataset.point}`;
                const fieldValue = currentVariant.metafields[field];
                //
                if (metafieldSource === 'variant') {
                    let found = false;
                    if (field.startsWith("tailored_to")) {
                        found = true;
                        // style background white/grey based on boolean
                        fieldValue !== "F" ? el.classList.remove("grey_background") : el.classList.add("grey_background");
                    }
                    if (field.startsWith("application_")) {
                        found = true;
                        const application = theme.artisticTile.translateApplication(
                            field.replaceAll('application_', ''), fieldValue
                        );
                        // style + update cells based on "No" value vs. Yes/other
                        if (application === "No") {
                            el.textContent = "No";
                            el.classList.remove("it_bold");
                            el.classList.add("grey_background");
                        } else {
                            el.textContent = application;
                            el.classList.add("it_bold");
                            el.classList.remove("grey_background");
                        }
                    }
                    if (field === "install_maint_instructions") {
                        found = true;
                        // link for maintenance instructions (not currently the correct field)
                        if (fieldValue === "TCNA Handbook") {
                            el.setAttribute("href", "https://tcnatile.com/products/publications/2024-tcna-handbook-for-ceramic-glass-and-stone-tile-installation/");
                            el.closest("tr").classList.remove("hidden");
                        } else if (fieldValue === "MIA DSDM") {
                            // double-check this syntax. not confirmed on a product yet.
                            el.setAttribute("href", "https://www.stonesofnorthamerica.com/technical/dsdm.cfm");
                            el.closest("tr").classList.remove("hidden");
                        } else {
                            // hide this row since AT guidelines links have not been set up yet
                            el.closest("tr").classList.add("hidden");
                        }
                    }
                    if (field === "Layout-Instructions") {
                        found = true;
                        if (fieldValue) {
                            el.classList.remove('grey_background');
                            el.innerHTML = fieldValue;
                        } else {
                            el.classList.add('grey_background');
                            el.innerHTML = '';
                        }
                    }
                    if (field.startsWith("line_drawing") || field.startsWith("human_scale_drawing")) {
                        found = true;
                        // image sources
                        if (fieldValue) el.setAttribute("src", fieldValue);
                    }
                    if (field.startsWith("variation")) {
                        found = true;
                        // variation - connecting vertical line
                        if (field === "variation-line") {
                            if (`${currentVariant.metafields["variation"]}` === `${el.dataset.variation}`) {
                                el.classList.add("active");
                            } else {
                                el.classList.remove("active");
                            }
                        } else {
                            // v1-v4 squares
                            const lineVert = el.parentNode.querySelector('.line-vert');
                            if (`${currentVariant.metafields["variation"]}` === `${el.dataset.variation}`) {
                                el.classList.add("v-active");
                                lineVert?.classList.add("active");
                            } else {
                                el.classList.remove("v-active");
                                lineVert?.classList.remove("active");
                            }
                        }
                    }
                    if (field.includes("literature_link") || field.includes("inventory_by_lot_link")) {
                        found = true;
                        const fastrLitLink = currentVariant.metafields['Fastr-Product-Literature-Link'];
                        const linkContent = isWSDevOnly && isSlab && fastrLitLink ? fastrLitLink : currentVariant.metafields[field];
                        el.setAttribute("href", linkContent);
                        el.setAttribute("target", "_blank");
                        toggleVisibility(el, !!linkContent, 'visually-');
                    }
                    if (field.includes("install_instructions_link")) {
                        found = true;
                        const variantLinkContent = currentVariant.metafields[field];
                        const productLinkContent = productData.metafields[field];
                        if (!variantLinkContent) {
                            if (productLinkContent) {
                                el.setAttribute("href", productLinkContent);
                                toggleVisibility(el, true, 'visually-');
                            } else {
                                toggleVisibility(el, false, 'visually-');
                            }
                        } else {
                            el.setAttribute("href", variantLinkContent);
                            toggleVisibility(el, true, 'visually-');
                        }
                    }
                    if (field === "sold_full_box" && el.classList.contains("packing-info")) {
                        found = true;
                        el.textContent = "Sold in Full Box Only";
                    }
                    if (field === "featured_description") {
                        found = true;
                        let featuredDescription;
                        if (currentVariant.metafields.featured_description) { // check variant-level first
                            featuredDescription = currentVariant.metafields.featured_description;
                        } else if (productData.metafields.featured_description) { // check product-level
                            featuredDescription = productData.metafields.featured_description;
                        } else { // fallback to product.description
                            featuredDescription = productData.description;
                        }
                        if (featuredDescription) {
                            toggleVisibility(el.parentNode, true, 'visually-');
                            el.innerHTML = featuredDescription;
                        } else {
                            toggleVisibility(el.parentNode, false, 'visually-');
                        }
                    }
                    if (field === "attributes_size") {
                        found = true;
                        if (currentVariant.metafields["Mosaic-Size"] && currentVariant.metafields["sq_ft_per_piece"]) {
                            el.textContent = `${currentVariant.metafields["Mosaic-Size"]}, ${currentVariant.metafields["sq_ft_per_piece"]} SF PER PC`;
                        } else {
                            el.textContent = currentVariant.metafields[field];
                        }
                    }
                    if (!found) {
                        // fallback
                        el.textContent = currentVariant.metafields[field];
                    }
                }
                if (field === "lot_data") {
                    const stockInformation = document.querySelector(".stock-information");
                    const lotData = currentVariant.metafields[field];
                    if (lotData) {
                        // update + show lot data table
                        sqFtLotSelectorBtns = [];
                        sqFtLots = [];
                        const lotPhotoData = theme.artisticTile.formatLotsInfoAndPhotos((currentVariant.metafields.Lot_Photos_and_Info || "").split("|"));
                        const sqFtLotSelectorBtnsContainer = document.createElement('div');
                        let tableMarkup = "";
                        lotData.replace(/\(/g, "##").replace(/\)/g, "").replace(/\[/g, "##").replace(/\]/g, "").split(",")
                            .forEach((row, index) => {
                                const inventoryLotRow = prepareInventoryLotRow(row, index, lotPhotoData, currentVariant);
                                if (inventoryLotRow) {
                                    tableMarkup += inventoryLotRow[0];
                                    if (inventoryLotRow[1]) {
                                        sqFtLotSelectorBtns.push(inventoryLotRow[1]);
                                        sqFtLotSelectorBtnsContainer.appendChild(inventoryLotRow[1]);
                                    }
                                    if (inventoryLotRow[2]) {
                                        sqFtLots.push(inventoryLotRow[2]);
                                    }
                                }
                            });
                        el.innerHTML = tableMarkup;

                        const isGroupedItems = productData.tags.find(tag => tag.includes('Grouped Item'));
                        const tileGroupedItems = document.querySelector('.tile-grouped-items');
                        toggleVisibility(tileGroupedItems, false, 'visually-');
                        if (isGroupedItems) {
                            toggleVisibility(tileGroupedItems, true, 'visually-');
                        } else {
                            toggleVisibility(tileGroupedItems, false, 'visually-');
                        }

                        const lotInventoryViewAll = document.querySelector('.lot-inventory-view-all');
                        const lotInventoryShowView = document.querySelector('.lot-inventory-show-view');
                        const lotInventoryBtns = document.querySelector('.lot-inventory-btns');
                        const lotInventoryOOS = document.querySelector('.lot-inventory-OOS');
                        const lotInventoryInquire = document.querySelector('.lot-inventory-inquire');
                        const lotInventoryCalculator = document.querySelector('.lot-inventory-calculator');
                        const pdpCtaLinks = document.querySelector('.pdp__cta-links');
                        if (!currentVariant.available) {
                            // has 0 inventory
                            toggleVisibility(lotInventoryViewAll, false, 'visually-');
                            toggleVisibility(lotInventoryShowView, false, 'visually-');
                            toggleVisibility(lotInventoryOOS, true, 'visually-');
                            toggleVisibility(lotInventoryBtns, false, 'visually-');
                            //
                            pdpCtaLinks.classList.add('center');
                            if (isTile && lotInventoryInquire && lotInventoryCalculator) {
                                toggleVisibility(lotInventoryInquire, true, 'visually-');
                                toggleVisibility(lotInventoryCalculator, false, 'visually-');
                            }
                            return;
                        } else {
                            // has inventory
                            if (isSlab) {
                                if (typeof currentVariant.metafields.Lot_Photos_and_Info !== 'undefined') {
                                    toggleVisibility(lotInventoryViewAll, true, 'visually-');
                                } else {
                                    toggleVisibility(lotInventoryViewAll, false, 'visually-');
                                }
                            }
                            if (isTile) {
                                if (lotInventoryInquire && lotInventoryCalculator) {
                                    if (typeof currentVariant.metafields.Lot_Photos_and_Info !== 'undefined') {
                                        toggleVisibility(lotInventoryViewAll, true, 'visually-');
                                    } else {
                                        toggleVisibility(lotInventoryViewAll, false, 'visually-');
                                    }
                                    toggleVisibility(lotInventoryInquire, false, 'visually-');
                                    toggleVisibility(lotInventoryCalculator, true, 'visually-');
                                }
                            }
                            toggleVisibility(lotInventoryShowView, true, 'visually-');
                            toggleVisibility(lotInventoryOOS, false, 'visually-');
                            toggleVisibility(lotInventoryBtns, true, 'visually-');
                            //
                            pdpCtaLinks.classList.remove('center');
                        }

                        // SELECTED LOT
                        if (isWSDevOnly) {
                            // show lot selector button
                            if (dom.lotSelectorModalOpen) {
                                dom.lotSelectorModalOpen.parentNode.parentNode.setAttribute('aria-hidden', "false");
                                dom.lotSelectorModalOpen.parentNode.parentNode.classList.remove('!hidden');
                            }
                            // clean selected lot
                            if (dom.lotSelector) {
                                cleanSelectedLot();
                                dom.lotSelector.innerHTML = '';
                                dom.lotSelector.appendChild(sqFtLotSelectorBtnsContainer);
                            }
                        } else {
                            // hide lot selector button
                            if (dom.lotSelectorModalOpen) {
                                dom.lotSelectorModalOpen.parentNode.parentNode.setAttribute('aria-hidden', "true");
                                dom.lotSelectorModalOpen.parentNode.parentNode.classList.add('!hidden');
                            }
                        }

                        // re-initialize lot image modal with new images
                        initializeLotModalSlider();

                        toggleVisibility(stockInformation, true, 'visually-'); // show if hidden
                    } else {
                        // hide table
                        toggleVisibility(stockInformation, false, 'visually-'); // hide if no data
                    }
                }
                //
            });

            if (metafieldSource === 'variant') {
                let arrInventoryImages = document.querySelectorAll('.stock-info__lot-image img')
                arrInventoryImages.forEach(el => el.src = el.src.replace(/(.*).*(\.)/i, '$1_360x$2'));
            }
        } catch (e) {
            console.error(e);
        }
    };

    const cleanSelectedLot = () => {
        if (dom.lotSelectorModalOpen) {
            sqFtLotSelectorBtns.map(btn => btn.classList.remove('selected'));
            sqFtSelectedLot = undefined;
            sqFtChosenLot = undefined;
            sqFtAutoChosenLotQty = null;
            removeATCMsg(selectors.ATCSuccessMessage);
            dom.lotChoose.disabled = true;
            toggleVisibility(dom.lotSelectedLabel, false);
            toggleVisibility(dom.lotSelectedRemove, false);
            dom.lotSelectorModalOpen.classList.remove('!justify-start');
            dom.lotSelectorModalOpen.classList.remove('bg-[#EFEFEE]');
            dom.lotSelectorModalOpen.innerHTML = `<span>Select Lot</span>`;
        }
    }

    const toggleSqFtCalculateSelectLotModal = (selectedLot = null, forceSelect = false) => {
        if (!dom.lotSelectorModal.classList.contains('hidden') || forceSelect) {
            // hide
            document.body.parentNode.classList.remove("no-scroll");
            toggleVisibility(dom.lotSelectorModal, false);
            if (selectedLot) {
                sqFtChosenLot = selectedLot;
                toggleVisibility(dom.lotSelectedLabel, true);
                toggleVisibility(dom.lotSelectedRemove, true);
                dom.lotSelectorModalOpen.classList.add('!justify-start');
                dom.lotSelectorModalOpen.classList.add('bg-[#EFEFEE]');
                dom.lotSelectorModalOpen.innerHTML = `<span class="!font-[family-name:Sweet_Sans_Bold,sans-serif] font-bold">${selectedLot.lotSerial}</span>`;
                removeATCMsg(selectors.ATCSuccessMessage);
                clearErrors();
                sqFtAutoChosenLotQty = null;
                reCalculate();
            }
        } else {
            // show
            document.body.parentNode.classList.add("no-scroll");
            toggleVisibility(dom.lotSelectorModal, true);
            dom.lotSelectorModalClose.focus();
        }
    }

    const autoSelectLot = (userInputValue, defaultLot = null) => {
        let autoLotMsg = defaultLot ? `Lot has been auto-selected` : '';
        let autoLot = defaultLot;
        if (autoLot === null || userInputValue > autoLot.lotQuantity) {
            let autoLotFound = false;
            sqFtLots.forEach(l => {
                if (!autoLotFound && userInputValue <= l.lotQuantity) {
                    autoLot = l;
                    autoLotMsg = `Lot has been auto-selected.`;
                    autoLotFound = true;
                }
            });
        }
        //
        if (autoLot) {
            sqFtAutoChosenLotQty = userInputValue;
            sqFtLotSelectorBtns.forEach(b => {
                if (b.dataset.lotSerial === autoLot.lotSerial) {
                    b.click();
                }
            });
            toggleSqFtCalculateSelectLotModal(autoLot, true);
            removeATCMsg(selectors.ATCSuccessMessage);
            showATCMessage(autoLotMsg, false);
            return true;
        }
        return false;
    }

    const validateSelectedLot = (userInputValue = null) => {
        if (unitOfMeasure === "SF" && isWSDevOnly) {
            userInputValue = userInputValue || getCalculatorInputValue();
            const qtyWasAutoAndDiff = sqFtAutoChosenLotQty !== null && sqFtAutoChosenLotQty !== userInputValue
            if (!sqFtChosenLot || qtyWasAutoAndDiff) {
                if (sqFtLots.length) {
                    autoSelectLot(userInputValue, sqFtLots[0]);
                } else {
                    showATCMessage(`Please select a preferred lot above before calculating.`);
                    showInputError(false, true);
                    throw Error("Not lot selected.");
                }
            }
            //
            if (sqFtChosenLot) {
                if (userInputValue > parseFloat(sqFtChosenLot.lotQuantity)) {
                    // not enough qty
                    if (overageApplied && sqFtLots.length) {
                        // if overageApplied try to auto select another one with enough qty
                        if (autoSelectLot(userInputValue)) {
                            return;
                        }
                    }
                    removeATCMsg(selectors.ATCErrorMessage);
                    showATCMessage(`The lot you have selected is too small to fill your order. Please adjust your quantity, select a different lot, or <a href="#" data-live-chat>contact us for assistance</a>.`);
                    showInputError(false, true);
                    dom.addToCart.disabled = true;
                    throw Error("Not enough qty");
                } else {
                    dom.addToCart.disabled = false;
                }
            }
        }
    }

    /**
     * @returns {number}
     */
    const getCalculatorInputValue = () => {
        let userInputValue;
        const calcInput = document.getElementById("sqFtNeeded");
        const length = Number(dom.sqFtInputLength.value);
        const width = Number(dom.sqFtInputWidth.value);
        if (showFormula && length > 0 && width > 0) {
            userInputValue = length * width;
        } else {
            userInputValue = calcInput.value ? Number(calcInput.value) : 0;
        }
        return userInputValue;
    }

    const reCalculate = () => {
        // if any calculation was already made re-calculate
        if (dom.calculate && dom.totalPrice) {
            const tp = parseFloat(dom.totalPrice.innerText);
            if (tp > 0) {
                dom.calculate.click();
            }
        }
    }

    const handleSqFtCalculate = (event) => {
        try {
            clearErrors();

            let userInputValue = 0;
            if (event && event.target.matches('input[type="number"]')) {
                // triggered by number input
                userInputValue = Number(event.target.value);
            } else {
                // triggered by checkbox or click on calculate, grab the calculator input value (if any)
                userInputValue = getCalculatorInputValue();
            }
            // double-check that it's at least 0
            userInputValue = userInputValue < 0 ? 0 : userInputValue;
            const match = `${userInputValue}`.match(/^(-?\d+)(\.\d{0,4})?/);
            if (match && match.length && match[0] !== `${userInputValue}`) {
                userInputValue = Number(match[0]);
            }
            let finalUserInputValue = userInputValue;

            // check if the checkbox is checked - calculate quantity
            overageApplied = false;
            if (dom.overage && dom.overage.checked) {
                // add overage and calculate quantity
                const overageAmount = userInputValue * (overagePercentage / 100);
                finalUserInputValue += overageAmount;
                overageApplied = true;
            }

            // pre-tariff
            validateSelectedLot(finalUserInputValue);

            atcUnitQty = Math.ceil(finalUserInputValue / sqFtPerUnit);

            // update all user inputs to match this value
            dom.sqFtInput.forEach((input) => (input.value = userInputValue));

            // clear errors, unless they still apply
            if (currentVariant.available && atcUnitQty <= currentVariant.inventory_quantity) {
                clearErrors();
            }

            // set total sq. ft. needed
            totalSqFtNeeded = Number(atcUnitQty * sqFtPerUnit).toFixed(4);

            // update quantity input - quantity doesn't exist on Slab/Tailored To products
            if (!inquireState) {
                dom.quantitySelect.value = String(atcUnitQty);
            }

            // update calculator messaging
            showCalculations();

            // check inventory status - quantity doesn't exist on Slab/Tailored To products
            if (!inquireState) {
                inventoryStatusCheck();
            }
        } catch (e) {
            console.error(e);
            return false;
        }
        return true;
    };

    const showCalculations = () => {
        try {
            // update other calculator data points here
            // determine plurality + unit
            dom.unitType.forEach((span) => {
                if (
                    (span.dataset.hasOwnProperty("plural") && atcUnitQty > 1) ||
                    atcUnitQty === 0 ||
                    span.dataset.hasOwnProperty("alwaysPlural")
                ) {
                    span.textContent = soldByFullBox ? "Boxes" : "Pieces";
                } else {
                    span.textContent = soldByFullBox ? "Box" : "Piece";
                }
            });

            // update overage span
            if (overageApplied) {
                dom.overageIndicator.classList.add("is-active");
            } else {
                dom.overageIndicator.classList.remove("is-active");
            }

            // units needed
            dom.unitsNeeded.textContent = atcUnitQty;

            // Update +/- input
            dom.addToCartQty.value = atcUnitQty;

            // sq ft per unit
            // dom.sqFtPerUnit.textContent = parseFloat(sqFtPerUnit);

            // total sqft - strip extraneous zeroes for better readability - not working currently
            dom.totalSqFt.textContent = parseFloat(totalSqFtNeeded);

            // total price - shows discounted price if customer has trade member status
            let price = currentVariant.price;
            if (sqFtSelectedLot) {
                const ppSqFt = sqFtSelectedLot.price * 100;
                dom.totalPrice.textContent = (Math.ceil(ppSqFt * totalSqFtNeeded) / 100).toFixed(2);
            } else {
                const priceWithDiscount = calculateCloseoutAndTradeDiscounts(price, null, false);
                const unitPrice = `${priceWithDiscount.discountPrice}` !== `${price}` ? parseFloat(priceWithDiscount.discountPrice) : parseFloat(price);
                // round up on the hundredth to avoid off-by-one-penny errors
                dom.totalPrice.textContent = (Math.ceil(unitPrice * atcUnitQty) / 100).toFixed(2);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleInlineQuantityChange = (event = null) => {
        try {
            if (event) {
                if (event.target.classList.contains('btn-add')) {
                    dom.inlineQuantitySelect.value++;
                } else if (dom.addToCartQty.value > 0) {
                    dom.inlineQuantitySelect.value--;
                }
            }
            //
            const newQuantity = Number(dom.inlineQuantitySelect.value);
            inventoryStatusCheck(newQuantity);
            // keep errors if the quantity is still too high
            if (newQuantity <= currentVariant.inventory_quantity) {
                clearErrors();
            }
            atcUnitQty = newQuantity <= 1 ? 1 : newQuantity;
            dom.inlineQuantitySelect.value = atcUnitQty;
        } catch (e) {
            console.error(e);
        }
    };

    const handleQtyChange = (event = null) => {
        try {
            clearErrors();

            validateSelectedLot();

            if (event) {
                if (event.target.classList.contains('btn-add')) {
                    dom.addToCartQty.value++;
                } else if (dom.addToCartQty.value > 0) {
                    dom.addToCartQty.value--;
                }
            }

            setTimeout(() => {
                const sqFt = dom.addToCartQty.value * sqFtPerUnit;
                dom.sqFtInputLength.value = (sqFt / 2).toFixed(4);
                dom.sqFtInputWidth.value = (sqFt / 2).toFixed(4);
                dom.sqFtInput.forEach((input) => (input.value = sqFt.toFixed(4)));
                dom.calculate.click();
                handleSqFtCalculate();
            }, 500);
        } catch (e) {
            console.error(e);
        }
    }

    const updateSampleButton = () => {
        try {
            const addSampleButton = document.querySelector(selectors.freeSample);
            if (!addSampleButton) return;

            // only update pricing for non-members who pay for samples
            if (!isTradeMember && sampleVariantData) {
                const samplePrice = document.querySelector(selectors.samplePrice);
                samplePrice.textContent = `- ${sampleVariantData.price}`;
            }

            // hide button if there is no SKU
            if (!sampleVariantData) {
                addSampleButton.disabled = true;
                addSampleButton.style.display = "none";
            } else if (!sampleVariantData.available) {
                // hide button if sample not available
                addSampleButton.disabled = true;
                addSampleButton.style.display = "none";
            } else if (sampleVariantData.available && sampleVariantData.id) {
                // show button if sample is available
                addSampleButton.disabled = false;
                addSampleButton.style.display = "block";
            }
        } catch (e) {
            console.error(e);
        }
    };

    const updateSlabInventory = () => {
        try {
            const viewInventory = document.querySelector(selectors.viewInventory);
            if (!viewInventory) {
                return;
            }
            const slabImageString = currentVariant.metafields.slab_images
                ? currentVariant.metafields.slab_images
                : productData.metafields.slab_images;
            const slabImages = slabImageString
                ? slabImageString.split(",").map((str) => str.trim())
                : undefined;

            if (!slabImages) {
                // disable + hide button if there is no slab inventory data
                viewInventory.disabled = true;
                viewInventory.style.display = "none";
            } else {
                // init global slider
                $slabModalSlider = $(selectors.slabModalSlider);

                // unslick
                if ($slabModalSlider.hasClass("slick-initialized")) {
                    $slabModalSlider.slick("unslick");
                }

                // parse slab title from handle (there may be a better way)
                const slabTitle = productData.handle
                    .split("-sl")[0]
                    .replaceAll("-", " ");

                // update gallery + initialize slider, etc.

                // GRID IMAGES
                dom.slabGrid.innerHTML = slabImages
                    .map((image) => {
                        const slug = image.substr(image.lastIndexOf("/") + 1);
                        const slabId = slug.substr(0, slug.lastIndexOf("."));
                        return `<div class="pdp__slab-image lazyload" data-image-src="${image}" data-slab-id="${slabId}" data-open-slab-modal tabindex="0">
                              <img class="visually-hidden" data-src="${image}" alt="Piece of slab inventory, displaying the unique grain of the slab">
                            </div>`;
                    }).join("");

                // MODAL SLIDES
                let slabSliderHTML = ``;
                slabImages.forEach((image) => {
                    const slug = image.substr(image.lastIndexOf("/") + 1);
                    const slabId = slug.substr(0, slug.lastIndexOf("."));
                    const slabSharingSourceUrl = encodeURIComponent(`https://artistictile.com/products/${productData.handle}?variant=${currentVariant.id}&interface=slab-view&slab=${slabId}`);
                    const slabSharingMediaUrl = encodeURI(image);
                    const slabSharingDescription = encodeURI(`A gorgeous slab of ${slabTitle} at Artistic Tile.`);
                    const shareBlock = `<div class="pdp__slab-share">
                              <div class="pdp__slab-share-title">Share:</div>
                              <div class="pdp__slab-share-wrapper">
                                <div class="pdp__slab-share-item">
                                  <a href="sms:?&body=${slabSharingSourceUrl}" title="Share via SMS">
                                    <img src="https://cdn.shopify.com/s/files/1/0272/6956/2456/files/icon-Share-SMS.svg?v=1606113104" alt="">
                                  </a>
                                </div>
                                <div class="pdp__slab-share-item">
                                  <a href="mailto:?body=${slabSharingSourceUrl}" title="Share via Email">
                                    <img src="https://cdn.shopify.com/s/files/1/0272/6956/2456/files/icon-Share-Email.svg?v=1606113269" alt="">
                                  </a>
                                </div>
                                <div class="pdp__slab-share-item">
                                  <a href="//www.facebook.com/sharer.php?u=${slabSharingSourceUrl}"
                                    target="_blank" title="Share via Facebook">
                                    <img src="https://cdn.shopify.com/s/files/1/0272/6956/2456/files/icon-Share-FB.svg?v=1606113104" alt="">
                                  </a>
                                </div>
                                <div class="pdp__slab-share-item">
                                  <a target="_blank"
                                    href="//pinterest.com/pin/create/button/?url=${slabSharingSourceUrl}&media=${slabSharingMediaUrl}&description=${slabSharingDescription}"
                                    title="Share via Pinterest">
                                    <img src="https://cdn.shopify.com/s/files/1/0272/6956/2456/files/icon-Share-Pintrest.svg?v=1606113104" alt="">
                                  </a>
                                </div>
                              </div>
                            </div>`;
                    slabSliderHTML += `<div class="slide lazyload" data-slab-image-slide data-slab-id="${slabId}">
                                    <img class="visually-hidden" data-src="${image}" alt="Close-up image of slab to display its unique grain" />
                                    <!-- Desktop Details Bar -->
                                    <div class="pdp__slab-detail-bar pdp__slab-detail-bar--desktop">
                                      <div class="pdp__slab-detail-bar--left">
                                        <span class="pdp__slab-title">${slabTitle}</span>
                                        <span class="pdp__slab-id">${!slabId.includes("Display-Photo") ? slabId : ""
                    }</span>
                                      </div>
                                      <div class="pdp__slab-detail-bar--right">
                                        ${shareBlock}
                                      </div>
                                    </div>
                                  </div>`; // mobile updates to data
                });
                dom.slabModalSlider.innerHTML = slabSliderHTML;

                lazyloader.init();

                // setup listener for slide count
                if (!slabCounterSetup) {
                    $slabModalSlider.on("init reInit afterChange",
                        (event, slick, currentSlide, nextSlide) => {
                            // currentSlide is undefined on init -- set it to 0 in this case (currentSlide is 0 based)
                            dom.slabSlideCounter.textContent = `${(currentSlide ? currentSlide : 0) + 1} of ${slick.slideCount}`;

                            const currentSlab = $(`${selectors.slabModalSlider} .slick-current`);
                            const slabId = currentSlab.data("slabId")
                                ? currentSlab.data("slabId")
                                : "";

                            const slabImage = currentSlab.find("img").data("src") || slabImages[0];
                            const slabSharingSourceUrl = encodeURI(`https://artistictile.com/products/${productData.handle}?variant=${currentVariant.id}&interface=slab-view&slab=${slabId}`);
                            const slabSharingMediaUrl = encodeURI(slabImage);
                            const slabSharingDescription = encodeURI(`Check out these beautiful slabs at Artistic Tile.`);
                            dom.slabShareMobile.innerHTML = `
              <div class="pdp__slab-share">
                <div class="pdp__slab-share-title">Share:</div>
                <div class="pdp__slab-share-wrapper">
                  <div class="pdp__slab-share-item">
                    <a href="sms:?&body=${slabSharingSourceUrl}">
                      <img src="https://cdn.shopify.com/s/files/1/0272/6956/2456/files/icon-Share-SMS.svg?v=1606113104" alt="">
                    </a>
                  </div>
                  <div class="pdp__slab-share-item">
                    <a href="mailto:?body=${slabSharingSourceUrl}">
                      <img src="https://cdn.shopify.com/s/files/1/0272/6956/2456/files/icon-Share-Email.svg?v=1606113269" alt="">
                    </a>
                  </div>
                  <div class="pdp__slab-share-item">
                    <a href="//www.facebook.com/sharer.php?u=${slabSharingSourceUrl}"
                      target="_blank">
                      <img src="https://cdn.shopify.com/s/files/1/0272/6956/2456/files/icon-Share-FB.svg?v=1606113104" alt="">
                    </a>
                  </div>
                  <div class="pdp__slab-share-item">
                    <a target="_blank"
                      href="//pinterest.com/pin/create/button/?url=${slabSharingSourceUrl}&media=${slabSharingMediaUrl}&description=${slabSharingDescription}">
                      <img src="https://cdn.shopify.com/s/files/1/0272/6956/2456/files/icon-Share-Pintrest.svg?v=1606113104" alt="">
                    </a>
                  </div>
                </div>
              </div>`;

                            const slabTitle = productData.handle
                                .split("-sl")[0]
                                .replaceAll("-", " ");
                            dom.slabDetailsMobile.innerHTML = `<span class="pdp__slab-title">${slabTitle}</span><span class="pdp__slab-id">${!slabId.includes("Display-Photo") ? slabId : ""}</span>`;
                        }
                    );
                    slabCounterSetup = true;
                    toggleVisibility(dom.slabSlideCounter, true, 'visually-');
                }

                // (re)init modal slider
                $slabModalSlider.slick(SLAB_GALLERY_OPTIONS);

                // show button
                viewInventory.disabled = false;
                viewInventory.style.display = "block";
            }
        } catch (e) {
            console.error(e);
        }
    };

    const showSlabView = () => {
        try {
            verticalScrollPosition = window.scrollY;
            window.scrollTo(0, 0);
            toggleVisibility(dom.productDisplay, false, 'visually-');
            toggleVisibility(dom.slabGridContainer, true, 'visually-');
            document.body.classList.add("slab-view-open");
            // save focused element
            focusedElement = document.activeElement;
            //focus first slab image
            const firstImage = dom.slabGridContainer.querySelector(
                selectors.openSlabModal
            );
            if (firstImage) firstImage.focus();

            slabViewActive = true;
        } catch (e) {
            console.error(e);
        }
    };

    const hideSlabView = () => {
        try {
            window.scrollTo(0, verticalScrollPosition);
            verticalScrollPosition = null;
            toggleVisibility(dom.slabGridContainer, false, 'visually-');
            toggleVisibility(dom.productDisplay, true, 'visually-');
            document.body.classList.remove("slab-view-open");
            slabViewActive = false;
            window.dispatchEvent(new Event("resize")); // fix gallery initialization bug if user arrives via a Slab Share URL
            if (focusedElement) focusedElement.focus(); // return focus to where it was before slab view opened
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Simplified ATC method for free samples
     */
    const addSampleToCart = () => {
        try {
            if (!sampleVariantData) return;

            // add to cart with line item properties
            $.post(
                "/cart/add.js",
                {
                    items: [
                        {
                            quantity: 1,
                            id: sampleVariantData.id,
                            properties: {
                                type: "sample",
                            },
                        },
                    ],
                },
                (data) => {
                    window.location.href = "/cart";
                },
                "json"
            ).fail((err) => {
                console.log("Error", err.responseText || err.statusText);
            });
        } catch (e) {
            console.error(e);
        }
    };

    const convertStockByUnit = (inventory) => {
        try {
            if (unitOfMeasure === "Each") {
                // EACH
                return `${inventory} units`;
            } else if (soldByFullBox) {
                // SQFT Product - BOX
                return `${String((sqFtPerBox * inventory).toFixed(4))} S/F`;
            } else {
                // SQFT Product - PIECE
                return `${String((sqFtPerPiece * inventory).toFixed(4))} S/F`;
            }
        } catch (e) {
            console.error(e);
        }
    };

    const showInquireButton = () => {
        try {
            // check if the inquire button is already present
            const existingInquireButton = document.querySelector(
                ".pdp__inquire-btn-container"
            );
            if (existingInquireButton) {
                return;
            }

            const inquireButton = document.createElement("div");
            inquireButton.classList.add("pdp__inquire-btn-container", "low-stock");
            inquireButton.innerHTML = `<a class="pdp__inquire-btn low-stock" href="#" data-inquire-link>Inquire</a>`;

            if (unitOfMeasure === "Each") {
                document.querySelector(".quantity-atc-block").classList.add("low-stock");
                dom.innerForm.appendChild(inquireButton);
            } else {
                const calculatorContent = document.querySelector(
                    ".pdp__calculator-content"
                );
                inquireButton.classList.add("calculator");
                calculatorContent.appendChild(inquireButton);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const inventoryStatusCheck = (qty = false) => {
        try {
            clearErrors();
            if (madeInAmerica) return; // don't show inventory on Made In America products
            // the inline QTY selector checks inventory status before allowing a change in the updated quantity
            let quantity = qty ? qty : Number(dom.quantitySelect.value);
            const variantQuantity = currentVariant.inventory_quantity;
            const restockDate = currentVariant.metafields.stock_available_date;
            // compare to quantity of current variant
            if (quantity > variantQuantity) {
                showATCMessage(
                    `Only ${convertStockByUnit(variantQuantity)} currently available. ${restockDate
                        ? `Restock ETA: ${restockDate}`
                        : 'Please adjust quantity or <a href="#" data-inquire-link>contact us</a> to order this quantity.'
                    }`
                );
                showInquireButton();
                showInputError();
                dom.addToCart.disabled = true;
            }
        } catch (e) {
            console.error(e);
        }
    };

    const addToCart = () => {
        try {
            clearErrors();
            validateSelectedLot();

            // INVENTORY STATUS CHECK - DONT ALLOW CUSTOMER TO ADD MORE THAN INVENTORY TO CART
            // get current quantity directly from select element
            const quantity = Number(dom.quantitySelect.value);
            if (quantity === 0) {
                showATCMessage(`Nothing to add to cart (quantity is zero). Please adjust quantity.`);
                showInputError();
                return;
            }

            // update line item property values for "SF" products
            updateLineItemPropertyFields();
            if (lotThresholdQuantity && Number(totalSqFtNeeded) > lotThresholdQuantity) {
                openConfirmationModal();
                return;
            } else {
                // remove the initials + the prop input for ATC
                // since ATC will redirect after the hidden input is added, this is probably not necessary
                validatedInitials = undefined;
                const existingInitialsInput = document.querySelector('[data-prop="multi-lot-signature"]');
                if (existingInitialsInput) existingInitialsInput.remove();
            }

            submitProductForm();
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * ------------------------------------------------
     * ------------------------------------------------
     * ------------------------------------------------
     * **/

    /**
     * This will update the Buy Box + Details to reflect the current product + trade status
     */
    const initializeVariableElements = () => {
        try {
            // grab product JSON
            assignProductData();

            // check if simple product (to use parent-level metafields)
            metafieldSource = document.querySelector(selectors.metafieldSource).dataset.metafieldSource;

            // check if inquire state override is needed
            const inquireEl = document.querySelector(selectors.inquireState);
            inquireState = inquireEl && inquireEl.dataset.inquireState == "true";

            // check if "Made in America" custom product behavior is needed
            const madeInAmericaEl = document.querySelector(selectors.madeInAmerica);
            madeInAmerica = madeInAmericaEl ? true : false;

            // check if customer is a trade member
            const tradeEl = document.querySelector(selectors.tradeMember);
            isTradeMember = tradeEl && tradeEl.dataset.tradeMember == "true";

            // calculate customer discount
            const discountEl = document.querySelector(selectors.discountPercentage);
            discountPercentage = isTradeMember && discountEl ? Math.abs(Number(discountEl.dataset.discountPercentage)) : null;

            const isSlabEl = document.querySelector("[data-is-slab]");
            if (isSlabEl) {
                isSlab = JSON.parse(isSlabEl.dataset.isSlab);
            }
            const isTileEl = document.querySelector("[data-is-tile]");
            if (isTileEl) {
                isTile = JSON.parse(isTileEl.dataset.isTile);
            }
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Fetch product data via AJAX to allow use of all metafield data (in product.json.liquid)
     */
    const prepareMetafields = async () => {
        try {
            if (!productMetafieldsData) {
                const productHandle = $(selectors.productHandle).data("productHandle");
                await $.get(`/products/${productHandle}?view=json`).done((response) => {
                    productMetafieldsData = JSON.parse(response);
                });
            }

            // get the variant ID from URL, if there is one
            const url = new URL(document.location);
            const variantId = url.searchParams.get("variant");

            // check for "calcOpen" and "wishlist" params (which means customer linked from wishlist)
            openCalculator = url.searchParams.get("calcOpen") === "true";
            wishlistId = url.searchParams.get("wishlist");

            // check that the variant exists
            const foundVariant = productData.variants.find((variant) => `${variant.id}` === `${variantId}`);

            // match URL-based variant, or default to first variant
            currentVariant = variantId && foundVariant ? foundVariant : productData.variants[0];

            // set to use parent-level metafields on 'simple' products
            productObject = metafieldSource === "product" ? productData : currentVariant;

            // adding variant quantity (for stock status functionality)
            const quantityMap = mapVariantQuantities();

            // adding sample availability (for free sample functionality)
            const sampleData = mapSampleAvailabilityData();

            // add product metafields from response data
            productData.metafields = productMetafieldsData.product_metafields;

            // add in extra data from the liquid (variant inventory + sample availability)
            productData.variants.forEach((variant) => {
                variant.metafields = productMetafieldsData.variant_metafields[variant.id];
                // add inventory_quantity data to variants, since its only available in Liquid by default
                if (quantityMap.hasOwnProperty(variant.id)) {
                    variant.inventory_quantity = quantityMap[variant.id]
                }
                if (metafieldSource !== "product" && variant.metafields.sample_sku) {
                    // add sample data (for variant-sourced metafield products -- not simple products)
                    const sampleSku = variant.metafields.sample_sku.toLowerCase();
                    if (sampleSku && sampleData.hasOwnProperty(sampleSku)) {
                        const sampleDataFromMap = sampleData[sampleSku];
                        variant.sample_data = {...sampleDataFromMap};
                    }
                }
            });

            // add single sample data to global variable
            if (metafieldSource === "product" && productObject.metafields.sample_sku) {
                const sampleSku = productObject.metafields.sample_sku.toLowerCase();
                if (sampleSku) {
                    sampleVariantData = {
                        id: sampleData[sampleSku].id,
                        available: sampleData[sampleSku].available,
                        price: sampleData[sampleSku].price,
                    };
                }

                // retroactively add the sample data to the current variant so the sample price shows properly on the button
                currentVariant.sample_data = {...sampleVariantData};
            }

            initializeGallerySliders();
            filterStackedGallery();
            initializeLotModalSlider();
            handleStickyWishlist();
            handleModalThumbsMobile();

            if (metafieldSource === "product") {
                // set up mixed lot messaging functionality (for simple products)
                mapThresholdQuantity();
            }

            // check metafield to determine closeout pricing state
            hasCloseoutPrice = !!productObject.metafields['Closeout-Discount'];
            closeoutDiscount = parseFloat(`${productObject.metafields['Closeout-Discount'] ?? 0}`);
            closeoutInvPerct = (100 - closeoutDiscount) / 100;

            // setup variables needed for calculator
            pricePerSqFt = Number(productObject.metafields.price_per_sq_ft).toFixed(2) || "N/A";
            sqFtPerPiece = Number(productObject.metafields.sq_ft_per_piece);
            sqFtPerBox = Number(productObject.metafields.sq_ft_per_box);
            unitOfMeasure = theme.artisticTile.translateUnitMeasure(productObject.metafields.unit_measure);
            overagePercentage = productObject.metafields.recommended_overage ? Number(productObject.metafields.recommended_overage.replace("%", "")) : 10;
            soldByFullBox = productObject.metafields.sold_full_box !== "F";
            sqFtPerUnit = soldByFullBox ? sqFtPerBox : sqFtPerPiece;
            // set total sq. ft. needed using QTY: 1
            totalSqFtNeeded = Number(atcUnitQty * sqFtPerUnit).toFixed(4);

            // build swatches from product media
            setupSwatchImages();

            // update page data for current variant, select buttons + scroll selected buttons into view
            if (metafieldSource === "product") {
                initGroupedProducts();
                // set proper inventory S/F math to circumvent liquid bug
                const buggedStockElement = document.querySelector("[data-inventory-fix]");
                if (buggedStockElement) {
                    const inventory = buggedStockElement.dataset.fixedInventory;
                    const sqFtPerUnit = buggedStockElement.dataset.fixedSqFtPerUnit;
                    buggedStockElement.textContent = `IN STOCK (${(inventory * sqFtPerUnit).toFixed(4)} S/F available)`;
                }
                if (inquireState) {
                    setAvailableStockForSlabs(true);
                }

                updateDetails();
            } else {
                // update the page data for initial variant
                updatePageData();
                updateGroutData();
                filterGallerySliders();
                filterStackedGallery();
                handleStickyWishlist();
                updateOptionButtons();
            }

            // set the sample variant based on sample_data property
            sampleVariantData = currentVariant.sample_data;
            updateSampleButton();
            updateSlabInventory();

            const interfaceParam = url.searchParams.get("interface");
            const slabViewId = url.searchParams.get("slab");
            if (`${interfaceParam}` === "slab-view") {
                showSlabView(); // show grid
                if (slabViewId) {
                    openSlabModal(slabViewId); // open modal to show matching slab
                } else {
                    // update calculator
                    showCalculations();
                }
            }

            // strip out the Slab Share URL params (for single-variant slab products)
            url.searchParams.delete("interface");
            url.searchParams.delete("slab");
            if (!url.searchParams.get("customer_posted")) {
                const updatedUrl = `${window.location.href.split("?")[0]}${url.search}`;
                window.history.replaceState({}, "", updatedUrl);
            }

            // buy box loaded - leave time for the variant options to update
            if (openCalculator) {
                showCalculator();
            }

            //
            const buyBoxInner = document.querySelector(selectors.buyBoxInner);
            buyBoxInner?.classList.remove('opacity-0');
        } catch (e) {
            console.error(e);
        }
    }

    /**
     * Cache certain elements to prevent frequent lookups
     */
    const cacheDom = () => {
        try {
            dom = {};

            const singleSelectors = [
                'productDisplay', 'slabGrid', 'slabGridContainer', 'stockMessage', 'stockSlabMessage',
                'slabOutOfStockMessage', 'imageSlider', 'imageStack', 'imageStackThumbs', 'globalVideoSlide',
                'imageModal', 'modalSliderThumbs', 'lotSlabModal', 'lotTileModal', 'slabImageModal',
                'slabDetailsMobile', 'slabShareMobile', 'slabModalSlider', 'slabSlideCounter',
                'confirmationModal', 'initialsInput', 'sku', 'skuContainer', 'productionLeadTime',
                'sqFtPerUnit', 'productForm', 'sqFtNeededContainer', 'sqFtNeededContainerFormula',
                'sqFtInputLength', 'sqFtInputWidth', 'innerForm', 'showFormula', 'addToCart', 'addToCartQty',
                'eachAddToCart', 'quantitySelect', 'inlineQuantity', 'inlineQuantitySelect', 'unitsNeeded',
                'totalSqFt', 'totalPrice', 'overage', 'calculate', 'lotSelectorModalOpen',
                'lotSelectorModalClose', 'lotSelectorModal', 'lotSelector', 'lotChoose', 'lotSelectedLabel',
                'lotSelectedRemove', 'overageIndicator', 'tooltip', 'closeTooltip', 'tooltipContent',
                'discountMessage', 'updateLotSerial', 'updateLotQty', 'notifyMe', 'copyToCLipMessage'
            ];

            const multiSelectors = [
                'productTitle', 'productSubtitle', 'pricePerSqFt', 'price', 'pricePerBox', 'priceContainer',
                'compareAtPriceContainer', 'discountPrice', 'priceContainerPerBox',
                'compareAtPriceContainerPerBox', 'discountPricePerBox', 'videoSlide', 'overagePercentage',
                'optionButton', 'optionGroup', 'optionsWrapper', 'sqFtInput', 'unitType',
                'priceUnitType', 'variableDetail', 'prop'
            ];

            // Single element querySelector
            singleSelectors.forEach(key => {
                dom[key] = document.querySelector(selectors[key]);
            });

            // Multiple element querySelectorAll
            multiSelectors.forEach(key => {
                dom[key] = document.querySelectorAll(selectors[key]);
            });

        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Instantiate event listeners
     * @param overrideIsSlab
     */
    const bindUIListeners = (overrideIsSlab = false) => {
        try {
            if (overrideIsSlab) {
                isSlab = true;
            }
            if (dom === undefined) {
                cacheDom();
            }

            document.addEventListener("click", (e) => {
                // change variant option
                if (
                    e.target.matches(selectors.optionButton) ||
                    e.target.matches(`${selectors.optionButton} *`)
                ) {
                    e.preventDefault();
                    const optionNum = e.target.closest(selectors.optionNum).dataset
                        .optionNum;
                    const optionGroup = e.target.closest(selectors.optionGroup).dataset
                        .optionGroup;
                    const optionValue = e.target.closest(selectors.optionButton).dataset
                        .optionValue;
                    handleVariantSelect(optionGroup, optionNum, optionValue);
                }

                // slide variant options
                if (
                    e.target.matches(selectors.optionSliderBtn) ||
                    e.target.matches(`${selectors.optionSliderBtn} *`)
                ) {
                    e.preventDefault();
                    const direction = e.target.closest(selectors.optionSliderBtn).dataset
                        .direction;
                    const wrapper = e.target
                        .closest(selectors.optionGroup)
                        .querySelector(selectors.optionsWrapper);
                    handleVariantSlide(wrapper, direction);
                }

                // add variant to cart
                if (e.target.matches(selectors.addToCart)) {
                    addToCart(currentVariant.id);
                }

                // add free sample
                if (
                    e.target.matches(selectors.freeSample) ||
                    e.target.matches(`${selectors.freeSample} *`)
                ) {
                    e.preventDefault();
                    addSampleToCart();
                }

                // open and close calculator
                if (
                    e.target.matches(selectors.openCalculator) ||
                    e.target.matches(`${selectors.openCalculator} *`)
                ) {
                    showCalculator();
                } else if (
                    e.target.matches(selectors.closeCalculator) ||
                    e.target.matches(`${selectors.closeCalculator} *`) ||
                    e.target.matches(".calculator-open .overlay")
                ) {
                    hideCalculator();
                }

                // hide and show calculator by sqft and l x w
                if (
                    e.target.matches(selectors.showFormula) ||
                    e.target.matches(`${selectors.showFormula} *`)
                ) {
                    e.preventDefault();
                    toggleFormula();
                }

                // handle +/- input
                if (
                    e.target.matches(selectors.btnSubtract) ||
                    e.target.matches(`${selectors.btnSubtract} *`) ||
                    e.target.matches(selectors.btnAdd) ||
                    e.target.matches(`${selectors.btnAdd} *`)
                ) {
                    e.preventDefault();
                    if (dom.addToCartQty) {
                        handleQtyChange(e);
                    } else if (dom.inlineQuantitySelect) {
                        handleInlineQuantityChange(e)
                    }
                }

                // open and close tooltip
                if (
                    e.target.matches(selectors.tooltip) ||
                    e.target.matches(`${selectors.tooltip} *`)
                ) {
                    // toggle tooltip
                    if (!dom.tooltip.classList.contains("is-open")) {
                        openTooltip();
                    } else {
                        closeTooltip();
                    }
                } else if (
                    e.target.matches(selectors.closeTooltip) ||
                    e.target.matches(`${selectors.closeTooltip} *`)
                ) {
                    closeTooltip();
                } else {
                    // close tooltip if open + user clicks another element
                    if (dom.tooltip && dom.tooltip.classList.contains("is-open")) closeTooltip();
                }

                // inquire links to open chat widget
                if (e.target.matches(selectors.inquireLink)) {
                    e.preventDefault();
                    // LiveChat JS API: https://developers.livechat.com/docs/extending-chat-widget/javascript-api/
                    if (LiveChatWidget) {
                        LiveChatWidget.call("maximize");
                    }
                }

                // open and close image gallery modal
                if (
                    e.target.matches(selectors.openZoom) ||
                    e.target.matches(`${selectors.openZoom} *`)
                ) {
                    openImageModal();
                } else if (
                    e.target.matches(selectors.closeZoom) ||
                    e.target.matches(`${selectors.closeZoom} *`) ||
                    e.target.matches(".gallery-modal-open .overlay")
                ) {
                    closeImageModal();
                }

                // open and close lot inventory modal
                if (
                    e.target.matches(selectors.openLotZoom) ||
                    e.target.matches(`${selectors.openLotZoom} *`)
                ) {
                    const el = e.target.closest(selectors.openLotZoom);
                    const lotIdentifier = el.dataset.lotSerial.trim() || null;
                    if (isSlab) {
                        openLotSlabModal(lotIdentifier);
                    }
                    if (isTile && el.dataset.variantId) {
                        const variantId = el.dataset.variantId.trim() || null;
                        const picIndex = el.dataset.picIndex;
                        openLotTileModal(lotIdentifier, variantId, picIndex);
                    }
                } else if (
                    e.target.matches(selectors.closeLotZoom) ||
                    e.target.matches(`${selectors.closeLotZoom} *`) ||
                    e.target.matches(".lot-modal-open .overlay")
                ) {
                    if (isSlab) {
                        closeLotSlabModal();
                    }
                    if (isTile) {
                        closeLotTileModal();
                    }
                }

                // open and close slab image gallery modal
                if (e.target.matches(selectors.openSlabModal)) {
                    // pass specific slab ID
                    openSlabModal(e.target.dataset.slabId);
                } else if (
                    e.target.matches(selectors.closeSlabModal) ||
                    e.target.matches(`${selectors.closeSlabModal} *`) ||
                    e.target.matches(".slab-modal-open .overlay")
                ) {
                    closeSlabModal();
                }

                // get initials for multi-lot confirmation
                if (e.target.matches(selectors.confirmInitials)) {
                    if (validatedInitials) {
                        addMultiLotToCart();
                    } else {
                        const initialsInput = document.querySelector(selectors.initialsInput);
                        validateInitialsEntry(initialsInput);
                    }
                }

                // close multi-lot confirmation modal
                if (
                    e.target.matches(selectors.closeConfirmationModal) ||
                    e.target.matches(`${selectors.closeConfirmationModal} *`) ||
                    e.target.matches(".confirmation-modal-open .overlay") ||
                    e.target.matches(".confirmation-modal-open .pdp__calculator") ||
                    e.target.matches("[data-live-chat]")
                ) {
                    closeConfirmationModal();
                }

                // slab lot link scroll
                if (e.target.matches(selectors.slabLotLink)) {
                    e.preventDefault();
                    const slabLotContainer = document.querySelector(
                        selectors.slabLotContainer
                    );
                    if (slabLotContainer) {
                        const heading = slabLotContainer.querySelector(".heading");
                        if (!heading.classList.contains("open")) heading.click(); // open accordion
                        // slabLotContainer.scrollIntoView(true, { behavior: "smooth" });
                        window.scroll({
                            top: slabLotContainer.offsetTop - 70,
                            behavior: "smooth",
                        });
                    }
                }

                // grouped items link scroll
                if (e.target.matches(selectors.tileGroupedItemsLink)) {
                    e.preventDefault();
                    const tileGroupContainer = document.querySelector(
                        selectors.tileGroupedItemsContainer
                    );
                    if (tileGroupContainer) {
                        const heading = tileGroupContainer.querySelector(".heading");
                        if (!heading.classList.contains("open")) heading.click(); // open accordion
                        // tileGroupContainer.scrollIntoView(true, { behavior: "smooth" });
                        window.scroll({
                            top: tileGroupContainer.offsetTop - 70,
                            behavior: "smooth",
                        });
                    }
                }

                // slab inventory gallery view
                if (e.target.matches(selectors.viewInventory) && !slabViewActive) {
                    showSlabView();
                } else if (
                    e.target.matches(selectors.hideSlabView) ||
                    e.target.matches(`${selectors.hideSlabView} *`)
                ) {
                    hideSlabView();
                }

                // notify me button - lock the body scroll when the modal is open
                if (e.target.matches(selectors.notifyMe)) {
                    document.body.style.overflow = "hidden";

                    const iframe = document.getElementById("klaviyo-bis-iframe");
                    // unlock the body scroll when modal is closed
                    iframe.contentDocument.body.addEventListener("click", (e) => {
                        if (e.target.matches(".klaviyo-bis-close")) {
                            document.body.style.overflow = "auto";
                        }
                    });
                }

                if (
                    e.target.matches(selectors.lotGridButton) ||
                    e.target.matches(`${selectors.lotGridButton} *`)
                ) {
                    e.preventDefault();
                    const el = e.target.closest(selectors.lotGridButton);
                    const variantId = el.dataset.variantId;
                    if (isSlab) {
                        const lotId = el.dataset.lotSlideId || el.dataset.lotSlabSlideId;
                        gotoLotSlabsGridSlider(variantId, lotId);
                    }
                    if (isTile) {
                        const lotId = el.dataset.lotSerial;
                        const picIndex = el.dataset.picIndex;
                        gotoLotTileSlider(variantId, lotId, picIndex);
                    }
                }

                if (
                    e.target.matches(selectors.viewAllLotsBtn) ||
                    e.target.matches(`${selectors.viewAllLotsBtn} *`)
                ) {
                    e.preventDefault();
                    const el = e.target.closest(selectors.viewAllLotsBtn);
                    const variantId = el.dataset.viewLotsBtn;
                    if (isSlab) {
                        gotoLotSlabsGrid(variantId, true);
                    }
                    if (isTile) {
                        const lotId = el.dataset.lotSerial;
                        gotoLotTilesGridSlider(variantId, lotId);
                    }
                }

                if (
                    e.target.matches(selectors.viewAllLotsTileBtn) ||
                    e.target.matches(`${selectors.viewAllLotsTileBtn} *`)
                ) {
                    e.preventDefault();
                    const el = e.target.closest(selectors.viewAllLotsTileBtn);
                    const variantId = el.dataset.viewLotsTileBtn;
                    if (isTile) {
                        const lotId = el.dataset.lotSerial;
                        gotoLotTilesGridAllLots(variantId, lotId);
                    }
                }

                if (
                    e.target.matches(selectors.viewAllLotsGridBtn) ||
                    e.target.matches(`${selectors.viewAllLotsGridBtn} *`)
                ) {
                    e.preventDefault();
                    const el = e.target.closest(selectors.viewAllLotsGridBtn);
                    const variantId = el.dataset.viewLotsBtn;
                    if (isTile) {
                        gotoLotTilesGrid(variantId, true);
                    }
                }

                if (
                    e.target.matches(selectors.lotSwitchGridNumbers) ||
                    e.target.matches(`${selectors.lotSwitchGridNumbers} *`)
                ) {
                    e.preventDefault();
                    const el = e.target.closest(selectors.lotSwitchGridNumbers);
                    if (el.classList.contains("inactive")) {
                        el.classList.remove("inactive");
                    } else {
                        el.classList.add("inactive");
                    }
                    const parent = el.closest("[data-lot-variant-wrapper]");
                    $(parent).find("[data-lot-number]").slideToggle();
                }
                if (
                    e.target.matches(selectors.lotSlideView) ||
                    e.target.matches(`${selectors.lotSlideView} *`)
                ) {
                    e.preventDefault();
                    const el = e.target.closest(selectors.lotSlideView);
                    const type = el.dataset.lotSlabSlideView;
                    const parent = el.closest("[data-lot-variant-wrapper]");
                    const children = parent.querySelectorAll(".pdp__lot-modal-slide__info");
                    $(el).addClass("active").siblings().removeClass("active");
                    children.forEach((child) => {
                        if (type === "full") {
                            child.classList.add("hide");
                        } else {
                            child.classList.remove("hide");
                        }
                    });
                    if (lotModalOpen) {
                        $lotSlabSlider.slick("setPosition");
                    }
                    if (slabModalOpen) {
                        $slabModalSlider.slick("setPosition");
                    }
                }
                if (
                    e.target.matches(selectors.lotShareBtn) ||
                    e.target.matches(`${selectors.lotShareBtn} *`)
                ) {
                    e.preventDefault();
                    copyUrlToClipboard();
                }

                if (
                    e.target.matches(selectors.lotModalInfoTooltipBtn) ||
                    e.target.matches(`${selectors.lotModalInfoTooltipBtn} *`)
                ) {
                    e.preventDefault();
                    const el = e.target.closest(selectors.lotModalInfoTooltipBtn);
                    const tooltip = el.parentElement.querySelector(
                        selectors.lotModalInfoTooltip
                    );
                    $(tooltip).fadeIn();
                }

                if (
                    e.target.matches(selectors.lotModalInfoTooltipClose) ||
                    e.target.matches(`${selectors.lotModalInfoTooltipClose} *`)
                ) {
                    e.preventDefault();
                    const el = e.target.closest(selectors.lotModalInfoTooltipClose);
                    const tooltip = el.parentElement;
                    $(tooltip).fadeOut();
                }

                if (
                    e.target.matches(selectors.lotModalInfoTooltipClose) ||
                    e.target.matches(`${selectors.lotModalInfoTooltipClose} *`)
                ) {
                    e.preventDefault();
                    const el = e.target.closest(selectors.lotModalInfoTooltipClose);
                    const tooltip = el.parentElement;
                    $(tooltip).fadeOut();
                }

                if (
                    e.target.matches(selectors.openChat) ||
                    e.target.matches(`${selectors.openChat} *`)
                ) {
                    e.preventDefault();
                    if (LiveChatWidget) {
                        $("#chat-widget-container").show();
                        LiveChatWidget.call("maximize");
                    }
                }
            });

            document.addEventListener("keydown", (e) => {
                // arrow key listener for slab modal accessibility (global key listener to prevent having to focus on the slider)
                if (lotModalOpen) {
                    if (e.code == "ArrowLeft") {
                        $lotSlabSlider.slick("slickPrev");
                    } else if (e.code == "ArrowRight") {
                        $lotSlabSlider.slick("slickNext");
                    } else if (e.code == "Escape") {
                        // close slab modal
                        closeLotSlabModal();
                    }
                } else {
                    if (
                        document.activeElement.matches(selectors.openLotZoom) &&
                        (e.code == "Enter" || e.code == "Space")
                    ) {
                        const lotIdentifier = document.activeElement.dataset.lotSerial.trim() || null;
                        openLotSlabModal(lotIdentifier);
                    }
                }
                // Keyboard Accessibility for Slab Inventory Feature
                // arrow key listener for slab modal accessibility (global key listener to prevent having to focus on the slider)
                if (slabModalOpen) {
                    if (e.code == "ArrowLeft") {
                        $slabModalSlider.slick("slickPrev");
                    } else if (e.code == "ArrowRight") {
                        $slabModalSlider.slick("slickNext");
                    } else if (e.code == "Escape") {
                        // close slab modal
                        closeSlabModal();
                    }
                } else if (document.activeElement.matches(selectors.openSlabModal)) {
                    // enter key listener for opening slab modal with keyboard only
                    if (e.code == "Enter") {
                        openSlabModal(document.activeElement.dataset.slabId);
                    } else if (e.code == "Escape") {
                        hideSlabView();
                    }
                }
            });

            if (dom.lotSelectorModalOpen && dom.lotSelectorModalClose && dom.lotChoose) {
                dom.lotSelectorModalOpen.addEventListener("click", () => {
                    toggleSqFtCalculateSelectLotModal();
                });
                dom.lotSelectorModalClose.addEventListener("click", () => {
                    toggleSqFtCalculateSelectLotModal();
                });
                dom.lotChoose.addEventListener("click", () => {
                    toggleSqFtCalculateSelectLotModal(sqFtSelectedLot);
                });
                dom.lotSelectedRemove.addEventListener("click", () => {
                    cleanSelectedLot();
                });
            }

            if (dom.overage) {
                dom.overage.addEventListener("change", handleSqFtCalculate);
            }
            if (dom.calculate) {
                dom.calculate.addEventListener("click", handleSqFtCalculate);
            }

            if (dom.inlineQuantitySelect) {
                dom.inlineQuantitySelect.addEventListener("input", () => {
                    handleInlineQuantityChange();
                });
            }
            if (dom.addToCartQty) {
                dom.addToCartQty.addEventListener("input", () => {
                    handleQtyChange();
                });
            }

            if (dom.initialsInput) {
                dom.initialsInput.addEventListener("input", debouncedInitialsValidation); // validate multi-lot initials
            }

            const limitDecimals = (e) => {
                const value = e.target.value;
                const match = value.match(/^(-?\d+)(\.\d{0,4})?/);
                if (match && match.length && match[0] !== value) {
                    e.target.value = match[0];
                }
            }
            if (dom.sqFtInput) {
                dom.sqFtInput.forEach((i) => {
                    i.addEventListener("input", limitDecimals);
                })
            }
            if (dom.sqFtInputWidth) {
                dom.sqFtInputWidth.addEventListener("input", limitDecimals);
            }
            if (dom.sqFtInputLength) {
                dom.sqFtInputLength.addEventListener("input", limitDecimals);
            }

            // prevent form submission with enter key (users can press enter from input field)
            $(document).on("keydown", 'input[type="number"]', function (event) {
                if (event.key == "Enter") {
                    event.target.blur();
                }
            });

            // keep the buy box either sticky or centered, depending on screen height
            const adjustBuyBox = () => {
                if (window.innerWidth < 1025) return;
                const buyBox = document.querySelector(selectors.buyBox);
                if (buyBox) {
                    const buyBoxInner = buyBox.querySelector(selectors.buyBoxInner);
                    // buy box content is taller than the viewport
                    if (buyBoxInner.offsetHeight >= window.innerHeight) {
                        // stick to bottom of viewport (default)
                        buyBox.classList.remove("centered");
                    } else if (
                        buyBox.offsetHeight + window.innerHeight * 0.2 >=
                        window.innerHeight
                    ) {
                        // stick to bottom of viewport still (since the buy box won't fit with the centered class)
                        buyBox.classList.remove("centered");
                    } else {
                        // sticky, about 20vh from the top of the screen
                        buyBox.classList.add("centered");
                    }
                }
            };
            window.addEventListener("DOMContentLoaded", adjustBuyBox);
            window.addEventListener("resize", debounce(adjustBuyBox, 250));
        } catch (e) {
            console.error(e);
        }
    };

    /**
     * Netatwork pushes all-caps product titles, which can't be fixed with CSS
     * this function changes 'PRODUCT TITLE' to 'Product Title'
     */
    const fixTitleCasing = () => {
        try {
            const capitalizeFirstLetter = (str) =>
                str.charAt(0).toUpperCase() + str.slice(1);
            const productTitle = dom.productTitle[0].textContent;
            const fixed = productTitle
                .split(" ")
                .map((str) => capitalizeFirstLetter(str.toLowerCase()))
                .join(" ");
            dom.productTitle.forEach(title => {
                title.textContent = fixed
            })
        } catch (e) {
            console.error(e);
        }
    };

    const initOptionSliderButtons = () => {
        try {
            dom.optionsWrapper.forEach((wrapper) => {
                if (wrapper.scrollWidth === wrapper.offsetWidth) {
                    wrapper.parentElement
                        .querySelectorAll(selectors.optionSliderBtn)
                        .forEach((btn) => btn.classList.add("hidden"));
                }
            });
        } catch (e) {
            console.error(e);
        }
    };

    const initRelatedProducts = () => {
        try {
            $(".related_products_slider").slick({
                dots: true,
                infinite: false,
                touchThreshold: 10,
                nextArrow: '<svg class="related_products_slider_arrow_right" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="8px" height="16px" viewBox="0 0 8 16" version="1.1"> <title>Path</title> <g id="Related-Products" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"> <g id="D---PDP---Related-Products" transform="translate(-1391.000000, -646.000000)" fill="#494742"> <g id="In-This-Collection" transform="translate(118.000000, 437.000000)"> <g id="Arrow" transform="translate(1258.000000, 198.000000)"> <g id="noun_Arrow_2335652" transform="translate(19.000000, 18.776119) rotate(-90.000000) translate(-19.000000, -18.776119) translate(11.000000, 14.776119)"> <polygon id="Path" points="7.99052632 8 15.78 0.311688312 15.4852632 0 7.99052632 7.3974026 0.495789474 0 0.18 0.311688312"/> </g> </g> </g> </g> </g> </svg>',
                prevArrow: '<svg  class="related_products_slider_arrow_left" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="8px" height="16px" viewBox="0 0 8 16" version="1.1"> <title>Path</title> <g id="Related-Products" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"> <g id="D---PDP---Related-Products" transform="translate(-132.000000, -646.000000)" fill="#494742"> <g id="In-This-Collection" transform="translate(118.000000, 437.000000)"> <g id="Arrow" transform="translate(18.500000, 217.000000) scale(-1, 1) translate(-18.500000, -217.000000) translate(0.000000, 198.000000)"> <g id="noun_Arrow_2335652" transform="translate(19.000000, 18.776119) rotate(-90.000000) translate(-19.000000, -18.776119) translate(11.000000, 14.776119)"> <polygon id="Path" points="7.99052632 8 15.78 0.311688312 15.4852632 0 7.99052632 7.3974026 0.495789474 0 0.18 0.311688312"/> </g> </g> </g> </g> </g> </svg>',
                slidesToShow: 4,
                slidesToScroll: 4,
                responsive: [
                    {
                        breakpoint: 768,
                        settings: {
                            dots: true,
                            arrows: false,
                            slidesToShow: 2,
                            slidesToScroll: 2,
                        },
                    },
                ],
            });
        } catch (e) {
            console.error(e);
        }
    };

    const checkIfShouldOpenLotSlabModal = () => {
        try {
            const current = getCurrentUrlAsObject();
            if (current.lotModalVariantId && current.lotModalLotId) {
                setTimeout(() => {
                    if (isSlab) {
                        openLotSlabModal(current.lotModalLotId);
                    }
                    if (isTile) {
                        openLotTileModal(current.lotModalLotId, current.lotModalVariantId);
                    }
                }, 1000);
            }
        } catch (e) {
            console.error(e);
        }
    };

    /**
     *  ------------------------------------------------
     *  ------------------------------------------------
     *  ------------------------------------------------
     *  **/

    const init = async () => {
        window.addEventListener("load", () => {
            handleStickyWishlist();
            handleModalThumbsMobile();
        });
        window.addEventListener("scroll", () => {
            handleStickyWishlist();
            handleAutoplayVideos();
        });
        window.addEventListener("resize", () => {
            handleStickyWishlist();
            handleModalThumbsMobile();
        });
        //
        cacheDom();
        initializeVariableElements();
        await prepareMetafields();
        bindUIListeners();
        fixTitleCasing();
        initOptionSliderButtons();
        initRelatedProducts();
        checkIfShouldOpenLotSlabModal();
    };

    return {
        init: init,
    };
})();